import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, inject, TemplateRef, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar, MatSnackBarConfig, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DATE_FORMATS, MatNativeDateModule } from '@angular/material/core';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { KeycloakService } from 'keycloak-angular';
import { SideEntityFormComponent as ParentSideEntityFormComponent } from '@shared-lib/chart-components/side-entity-form/side-entity-form.component';
import { SpricedFilterDialogComponent } from '@shared-lib/global-components/spriced-filter-dialog/spriced-filter-dialog.component';
import { ConfigService } from '@shared-lib/shared-services/config.service';
import { ENVIRONMENT_CONFIG, EnvironmentConfig } from '@shared-lib/shared-services/environment-config.token';
import { GlobalFilterService } from '@shared-lib/shared-services/global-filter-service';
import { ResponsiveLayoutService } from '@shared-lib/shared-services/responsive-layout.service';
import { catchError, forkJoin, map, Observable, of, shareReplay, Subscription, switchMap } from 'rxjs';
import { getEntityFollowUpSaveHandler } from './save-handlers/entity-follow-up-save-handlers';
import {
  mapRerAuditSourceData,
  RER_AUDIT_ENTITY,
  RER_AUDIT_SOURCE_FIELDS,
  RER_PA_HISTORY_ENTITIES,
} from '../rer-audit-record.utils';

const DATE_DISPLAY_FORMATS = {
  parse: { dateInput: null },
  display: {
    dateInput: { month: '2-digit', day: '2-digit', year: 'numeric' },
    monthYearLabel: { month: 'short', year: 'numeric' },
    dateA11yLabel: { month: 'long', day: 'numeric', year: 'numeric' },
    monthYearA11yLabel: { month: 'long', year: 'numeric' },
  },
};

@Component({
  selector: 'app-side-entity-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatSnackBarModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatTooltipModule,
    MatDialogModule,
  ],
  providers: [
    { provide: MAT_DATE_FORMATS, useValue: DATE_DISPLAY_FORMATS },
  ],
  templateUrl: './side-entity-form.component.html',
  styleUrls: ['./side-entity-form.component.scss'],
})
export class SideEntityFormComponent extends ParentSideEntityFormComponent {
  private static readonly DEBUG_LOG_KEY = 'PALANTIR_CONFIG_SAVE_DEBUG_V2';
  private static readonly TABLE_REFRESH_EVENT = 'palantir-table-refresh-request';
  private static readonly FORM_SCHEMA_EVENT = 'palantir-form-schema-change';
  private static readonly QUOTE_FORM_POPUP_EVENT = 'palantir-quote-form-popup-request';
  private static readonly QUOTE_DETAIL_FETCH_API_URL = '/spriced/platform/cummins_quote/cummins_quote_fetch';
  private static readonly GENERIC_SAVE_API_URL = '/spriced/platform';
  private static readonly GENERIC_SAVE_API_ENTITIES = new Set([
    RER_AUDIT_ENTITY,
    'cu_hp_fuel_range_definition',
    'custom_rer_suggested',
    'cu_suggested_prices_rer_engine',
  ]);
  private static readonly GOMS_CUSTOMER_ENTITY_KEYS = new Set([
    'cu_supp_s_goms_customer',
    's_goms_customer',
    's_goms customer',
  ]);
  private static readonly POST_SAVE_READ_FUNCTION_BY_ENTITY: Record<string, string> = {
    cummins_quote: 'NestedDynamicReadServices',
    custom_rer_engine_pricing: 'CuRERSuggestPrice',
    cu_rer_engine_pricing: 'CuRERSuggestPrice',
    'rer engine pricing': 'CuRERSuggestPrice',
  };
  private static readonly ENTITY_KEY_BY_DISPLAY_LABEL: Record<string, string> = {
    quote: 'cummins_quote',
    'quote details': 'cummins_quote_detail',
    'pa history': RER_AUDIT_ENTITY,
    'rer audit': RER_AUDIT_ENTITY,
    'rer pa history': RER_AUDIT_ENTITY,
    'rer fuel rating definition': 'cu_hp_fuel_range_definition',
    'rer suggested price': 'custom_rer_suggested',
    'rer engine pricing': 'custom_rer_engine_pricing',
  };
  private static readonly ALWAYS_HIDDEN_FIELD_TOKENS = new Set(['cpm']);
  private static readonly HIDDEN_FIELD_TOKENS = new Set(['is valid', 'created by', 'created date', 'updated by', 'updated date', 'uuid', 'cpm']);
  private static readonly FUEL_RATING_DEFINITION_FIELD_ORDER = new Map([
    ['range description', 0],
    ['minimum horsepower', 1],
    ['maximum horsepower', 2],
    ['active indicator', 3],
  ]);
  private static readonly RER_PA_HISTORY_FIELD_ORDER = new Map([
    ['code', 0],
    ['pricing action', 1],
    ['change reason', 2],
    ['old price', 3],
    ['suggested future price', 4],
    ['current price', 5],
    ['current effective date', 6],
    ['notes', 7],
  ]);
  private static readonly RER_PA_HISTORY_FIELD_TOKENS = new Set(
    RER_AUDIT_SOURCE_FIELDS.map((field) => field.replace(/_/g, ' ')),
  );
  private static readonly PREFERRED_FIELD_ORDER = [
    'name',
    'code',
    'goms',
    'quote year',
    'pricing region',
    'quote region',
    'product bu',
    'pricing admin owner',
    'bill tos',
    'sap bill to code ship to',
    'parts manager',
    'perform impact',
    'quote status',
    'link to impact report',
    'impact run date',
    'final impact',
    'get file',
    'link to goms outbound file',
    'submit for approval',
    'has waiver approved',
    'regional marketing leader approval',
    'local finance marketing controller approval',
    'account number',
    'regional director or manager approval',
    'global parts marketing director approval',
    'finance director approval',
    'exec director approval',
    'exec finance director approval',
    'vice president approval',
    'president or director approval',
    'approvals status',
    'status',
    'publish to sap date',
    'sap resend flag',
    'resend for approval',
    'denial reason',
    'link to request',
    'notes',
    'static units',
    'dynamic units',
    'final marketing impact',
  ];
  private static readonly ENTITY_HIDDEN_FIELD_TOKENS = new Map<string, Set<string>>([
    [RER_AUDIT_ENTITY, new Set(['region', 'regional pricing flag'])],
    ['custom_rer_suggested', new Set(['is deleted'])],
    ['cu_suggested_prices_rer_engine', new Set(['is deleted'])],
  ]);
  private readonly customHttp: HttpClient;
  private readonly customCdr: ChangeDetectorRef;
  private readonly customSnackBar: MatSnackBar;
  private readonly customConfigService: ConfigService;
  private quoteYesNoFieldTokenCache?: Set<string>;
  private quoteYesNoFieldConfigCache?: Map<string, any>;
  private quoteYesNoFieldTokenCacheKey = '';
  private quoteYesNoOptionsCache = new Map<string, Array<{ label: string; value: any }>>();
  private formStringFieldTokenCache?: Set<string>;
  private formStringFieldTokenCacheKey = '';
  private headerOrderCache = new Map<string, Observable<string[] | null>>();
  private readonly defaultQuoteYesNoOptions = [
    { label: 'Yes', value: 'Yes' },
    { label: 'No', value: 'No' },
  ];
  private quoteDetailFetchSubscription?: Subscription;
  private quoteDetailDropdownSubscription?: Subscription;
  private readonly keycloakServiceOverride: KeycloakService = inject(KeycloakService);
  private readonly environmentConfig = inject<EnvironmentConfig | null>(
    ENVIRONMENT_CONFIG,
    { optional: true },
  );
  @ViewChild('partNumberDialog') private partNumberDialog?: TemplateRef<unknown>;
  private partNumberDialogRef?: any;
  private appliedPartNumberFilterPayload: any = null;
  activePartNumberFieldKey = '';
  pendingPartNumberOption: any = null;
  partNumberCurrentPage = 1;

  constructor(
    globalFilterService: GlobalFilterService,
    http: HttpClient,
    configService: ConfigService,
    snackBar: MatSnackBar,
    responsiveLayoutService: ResponsiveLayoutService,
    cdr: ChangeDetectorRef,
    private readonly dialog: MatDialog,
  ) {
    super(
      globalFilterService,
      http,
      configService,
      snackBar,
      responsiveLayoutService,
      cdr,
    );
    this.customHttp = http;
    this.customCdr = cdr;
    this.customSnackBar = snackBar;
    this.customConfigService = configService;
  }

  private readonly dateValueCache = new Map<string, Date | null>();

  /** Route domain-reference grid buttons to the external sPriced entity page. */
  override navigateToDomainReference(fieldKey: string): void {
    const entity = (this as any).domainAttributeEntities?.get(fieldKey);
    const baseUrl = String(this.environmentConfig?.SPRICED_DATA_URL || '').trim();

    if (!entity || !baseUrl) {
      super.navigateToDomainReference(fieldKey);
      return;
    }

    const redirectUrl = new URL(baseUrl, window.location.origin);
    redirectUrl.searchParams.set('entity', String(entity));
    window.open(redirectUrl.toString(), '_self');
  }

  /**
   * Returns a stable Date reference for a given stored value so the date
   * input's [value] binding does not see a "new" object on every change
   * detection cycle. Without this, an already-populated date would be
   * re-written into the input mid-edit, making it impossible to type over.
   */
  override getDateValue(value: any): Date | null {
    if (value instanceof Date) {
      return super.getDateValue(value);
    }

    const key = value === null || value === undefined ? '' : String(value);
    if (this.dateValueCache.has(key)) {
      return this.dateValueCache.get(key) ?? null;
    }

    const resolved = super.getDateValue(value);
    this.dateValueCache.set(key, resolved);
    return resolved;
  }

  /**
   * Masks manual keyboard entry in the date inputs so users can type the
   * date as numbers and have it auto-formatted to MM/DD/YYYY. Non-digit
   * characters are stripped, and slashes are inserted automatically. The
   * datepicker still parses the completed value on blur via (dateChange).
   */
  onDateInputMask(fieldKey: string, event: Event): void {
    const input = event.target as HTMLInputElement | null;
    if (!input) {
      return;
    }

    const digits = input.value.replace(/\D/g, '').slice(0, 8);
    let formatted = digits;
    if (digits.length > 4) {
      formatted = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
    } else if (digits.length > 2) {
      formatted = `${digits.slice(0, 2)}/${digits.slice(2)}`;
    }

    if (input.value !== formatted) {
      input.value = formatted;
    }

    // When the field is cleared, null the model immediately so the save
    // sends null instead of relying on the datepicker's blur event.
    if (!digits) {
      this.onFieldValueChange(fieldKey, null);
    }
  }

  private getBottomRightSnackBarConfig(
    duration = 10000,
    panelClass?: string[],
  ): MatSnackBarConfig {
    return {
      duration,
      horizontalPosition: 'end',
      verticalPosition: 'bottom',
      panelClass,
    };
  }

  protected override generateFormFieldsFromRecord(): void {
    this.normalizeRerAuditFormRecords();
    super.generateFormFieldsFromRecord();
    this.ensureRerEnginePricingSuggestedFuturePriceField();
    this.filterHiddenFormFields();
    this.ensureQuoteActionFields();
    this.applySpecialFormFieldLabels();
    this.applyPreferredFieldOrder();
    this.applyConfiguredFieldOrder();
    this.preloadSelectedDomainFields();
    this.publishFormSchemaToTable();
  }

  private normalizeRerAuditFormRecords(): void {
    if (!this.isRerAuditFormEntity()) {
      return;
    }

    if (this.isRawRerAuditRecord(this.editableRecord)) {
      this.editableRecord = mapRerAuditSourceData(this.editableRecord);
    }
    if (this.isRawRerAuditRecord(this.originalRecord)) {
      this.originalRecord = mapRerAuditSourceData(this.originalRecord);
    }
  }

  private isRawRerAuditRecord(record: any): boolean {
    return !!record && typeof record === 'object' && (
      Object.prototype.hasOwnProperty.call(record, 'source_data') ||
      Object.prototype.hasOwnProperty.call(record, 'deam_id')
    );
  }

  private isRerAuditFormEntity(): boolean {
    const component = this as any;
    const currentFilters = component.globalFilterService?.getCurrentFilters?.();
    const candidates = [
      component.selectedEntityValue,
      this.normalizeFilterCandidate(currentFilters?.requestParams?.entity),
      this.normalizeFilterCandidate(currentFilters?.queryParams?.entity),
      component.data?.selectedEntity,
      component.data?.requestData?.entity,
      component.data?.entityName,
      component.editableRecord?.entity,
      component.originalRecord?.entity,
    ];

    return candidates.some((candidate) =>
      RER_PA_HISTORY_ENTITIES.has(String(candidate || '').trim().toLowerCase()),
    );
  }

  private ensureRerEnginePricingSuggestedFuturePriceField(): void {
    const entity = String(this.resolveSelectedEntityKeyForHeaderOrder() || '').trim().toLowerCase();
    if (
      !['custom_rer_engine_pricing', 'cu_rer_engine_pricing', 'rer engine pricing'].includes(entity) ||
      !Array.isArray(this.formFields) ||
      this.formFields.length === 0
    ) {
      return;
    }

    const fieldKey = 'suggested_future_price';
    const hasField = this.formFields.some((field: any) =>
      String(field?.key || '').trim().toLowerCase() === fieldKey,
    );
    if (!hasField) {
      this.formFields = [
        ...this.formFields,
        { key: fieldKey, label: 'Suggested Future Price' },
      ];
    }

    if (this.editableRecord && !Object.prototype.hasOwnProperty.call(this.editableRecord, fieldKey)) {
      this.editableRecord[fieldKey] = null;
    }
    if (this.originalRecord && !Object.prototype.hasOwnProperty.call(this.originalRecord, fieldKey)) {
      this.originalRecord[fieldKey] = null;
    }
  }

  private publishFormSchemaToTable(): void {
    if (typeof window === 'undefined' || !Array.isArray(this.formFields)) {
      return;
    }

    const configuredTargets = this.data?.formSchemaTargetWidgetIds || this.data?.filterListner || [];
    const targetWidgetIds = (Array.isArray(configuredTargets) ? configuredTargets : [configuredTargets])
      .map((widgetId: any) => String(widgetId || '').trim())
      .filter(Boolean);
    if (targetWidgetIds.length === 0) {
      return;
    }

    const component = this as any;
    const domainFieldKeys = Array.from(component.domainAttributeKeys || []) as string[];
    const domainOptions = domainFieldKeys.reduce((optionsByField: Record<string, any[]>, fieldKey) => {
      optionsByField[fieldKey] = Array.isArray(component.fieldDropdownOptions?.[fieldKey])
        ? component.fieldDropdownOptions[fieldKey]
        : [];
      return optionsByField;
    }, {});

    window.dispatchEvent(new CustomEvent(SideEntityFormComponent.FORM_SCHEMA_EVENT, {
      detail: {
        targetWidgetIds,
        entity: this.resolveSelectedEntityKeyForHeaderOrder(),
        fields: this.formFields.map((field: any) => ({ key: field?.key, label: field?.label })),
        domainFieldKeys,
        domainOptions,
      },
    }));
  }

  private preloadSelectedDomainFields(): void {
    this.formFields.forEach((field) => {
      const value = this.editableRecord?.[field.key];
      const hasSelectedValue = value !== null && value !== undefined && value !== '';

      if (hasSelectedValue && this.hasFieldDropdown(field.key)) {
        this.onFieldDropdownOpened(field.key, true);
      }
    });
  }

  getSelectedDomainFieldValue(fieldKey: string): any {
    const selectedRowValue = this.editableRecord?.[fieldKey];

    if (this.isGomsCustomerOemField(fieldKey)) {
      const selectedCode = this.getGomsCustomerOemCode(selectedRowValue);
      const matchingOption = ((this as any).fieldDropdownOptions?.[fieldKey] || []).find((option: any) =>
        this.compareGomsCustomerOemCodes(this.getGomsCustomerOemCode(option), selectedCode)
      );

      return matchingOption
        ? this.getFieldDropdownValueForField(fieldKey, matchingOption)
        : selectedCode;
    }

    const matchingOption = this.findDomainOptionForRecordValue(
      selectedRowValue,
      (this as any).fieldDropdownOptions?.[fieldKey] || [],
    );

    return matchingOption
      ? this.getFieldDropdownValue(matchingOption)
      : this.getFieldSelectedValue(selectedRowValue);
  }

  private findDomainOptionForRecordValue(selectedValue: any, options: any[]): any | undefined {
    if (!Array.isArray(options) || selectedValue === null || selectedValue === undefined || selectedValue === '') {
      return undefined;
    }

    if (typeof selectedValue === 'object') {
      // Domain records contain both a business code and an internal row ID. Those
      // numbers can overlap across different rows, so only compare like-for-like
      // properties and prefer the business code rendered by the table.
      const identityGroups = [
        ['code'],
        ['uuid'],
        ['id'],
        ['value'],
        ['name', 'displayname', 'display_name', 'label'],
      ];

      for (const keys of identityGroups) {
        const selectedIdentities = this.getDomainIdentityValues(selectedValue, keys);
        if (selectedIdentities.size === 0) {
          continue;
        }

        const match = options.find((option: any) => {
          const optionIdentities = this.getDomainIdentityValues(option, keys);
          return Array.from(selectedIdentities).some((identity) => optionIdentities.has(identity));
        });
        if (match) {
          return match;
        }
      }

      return undefined;
    }

    const scalarIdentity = this.normalizeDomainIdentity(selectedValue);
    if (!scalarIdentity) {
      return undefined;
    }

    // Scalar table values are normally business codes/labels. Check those before
    // falling back to the dropdown's configured value (usually its internal ID).
    return options.find((option: any) =>
      this.getDomainIdentityValues(option, ['code']).has(scalarIdentity),
    ) ?? options.find((option: any) =>
      this.normalizeDomainIdentity(this.getFieldDropdownLabel(option)) === scalarIdentity,
    ) ?? options.find((option: any) =>
      this.normalizeDomainIdentity(option?.value) === scalarIdentity,
    );
  }

  private getDomainIdentityValues(value: any, keys: string[]): Set<string> {
    const identities = new Set<string>();
    if (!value || typeof value !== 'object') {
      return identities;
    }

    const normalizedKeys = new Set(keys.map((key) => key.toLowerCase()));
    const sources = [value, value?.data].filter(
      (source, index, all) => source && typeof source === 'object' && all.indexOf(source) === index,
    );

    sources.forEach((source: any) => {
      Object.entries(source).forEach(([key, candidate]) => {
        if (!normalizedKeys.has(String(key).trim().toLowerCase())) {
          return;
        }
        const normalized = this.normalizeDomainIdentity(candidate);
        if (normalized) {
          identities.add(normalized);
        }
      });
    });

    return identities;
  }

  private normalizeDomainIdentity(value: any): string {
    return value === null || value === undefined || typeof value === 'object'
      ? ''
      : String(value).trim().toLowerCase().replace(/\s+/g, ' ');
  }

  getFieldDropdownValueForField(fieldKey: string, option: any): any {
    if (this.isGomsCustomerOemField(fieldKey)) {
      return this.getGomsCustomerOemCode(option);
    }

    return this.getFieldDropdownValue(option);
  }

  private isGomsCustomerOemField(fieldKey: string): boolean {
    if (String(fieldKey || '').trim().toLowerCase() !== 'oem_flag') {
      return false;
    }

    const selectedEntity = String(this.resolveSelectedEntityKeyForSave() || '').trim().toLowerCase();
    return SideEntityFormComponent.GOMS_CUSTOMER_ENTITY_KEYS.has(selectedEntity);
  }

  private getGomsCustomerOemCode(value: any, seen = new WeakSet<object>()): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    if (typeof value !== 'object') {
      const text = String(value).trim();
      const labelCodeMatch = text.match(/\{\s*(-?\d+)\s*\}\s*$/);
      if (labelCodeMatch) {
        return Number(labelCodeMatch[1]);
      }

      const numericValue = Number(text);
      return text !== '' && Number.isFinite(numericValue) ? numericValue : null;
    }

    if (seen.has(value)) {
      return null;
    }
    seen.add(value);

    // Normalized dropdown `value` is the domain row ID. The OEM value is
    // the number displayed in braces, including when code/name are separate.
    const displayedCode = this.formatAttributeLabel(value).match(/\{\s*(-?\d+)\s*\}\s*$/);
    if (displayedCode) {
      return Number(displayedCode[1]);
    }

    const data = value?.data ?? value;
    const candidates = [
      value?.value,
      data?.value,
      value?.code,
      data?.code,
      value?.id,
      data?.id,
      value?.label,
      data?.label,
    ];

    for (const candidate of candidates) {
      const resolved = this.getGomsCustomerOemCode(candidate, seen);
      if (resolved !== null) {
        return resolved;
      }
    }

    return null;
  }

  private compareGomsCustomerOemCodes(left: any, right: any): boolean {
    return left !== null && right !== null && String(left) === String(right);
  }

  override onFieldValueChange(fieldKey: string, value: any): void {
    if (this.isFieldReadonly(fieldKey)) {
      return;
    }

    super.onFieldValueChange(fieldKey, value);
  }

  onFieldDropdownSelectionChange(fieldKey: string, value: any): void {
    this.onFieldValueChange(fieldKey, value);

    if (!this.isQuoteDetailQuoteIdField(fieldKey)) {
      return;
    }

    this.quoteDetailFetchSubscription?.unsubscribe();
    this.quoteDetailFetchSubscription = undefined;
    this.quoteDetailDropdownSubscription?.unsubscribe();
    this.quoteDetailDropdownSubscription = undefined;

    const quoteCode = this.resolveSelectedQuoteCode(value);
    if (!quoteCode) {
      return;
    }

    this.quoteDetailFetchSubscription = this.customConfigService.getApi(
      SideEntityFormComponent.QUOTE_DETAIL_FETCH_API_URL,
      {},
      {
        rawPayload: { code: quoteCode },
        useQueryParam: false,
      },
      'post',
    ).subscribe({
      next: (response) => this.prepopulateQuoteDetailFields(response),
      error: (error) => {
        console.error('Failed to fetch Quote Detail values for the selected Quote ID.', error);
        this.customSnackBar.open('Unable to load details for the selected Quote ID.', 'Close', {
          duration: 10000,
        });
      },
    });
  }

  private isQuoteDetailQuoteIdField(fieldKey: string): boolean {
    const entity = this.normalizeQuoteFieldToken(this.resolveSelectedEntityKeyForSave());
    const field = this.normalizeQuoteFieldToken(fieldKey).replace(/\s+/g, '');
    return (entity === 'quote details' || entity === 'cummins quote detail') && field === 'quoteid';
  }

  private resolveSelectedQuoteCode(value: any): string | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    if (typeof value !== 'object') {
      return String(value).trim() || null;
    }

    const data = value?.data ?? value;
    const code = this.getValueByNormalizedKey(data, ['code', 'quoteid']) ??
      this.getValueByNormalizedKey(value, ['code', 'quoteid', 'value', 'id']);
    return code === null || code === undefined ? null : String(code).trim() || null;
  }

  private prepopulateQuoteDetailFields(response: any): void {
    if (!this.editableRecord) {
      return;
    }

    const responseRecord = this.findQuoteDetailResponseRecord(response);
    if (!responseRecord) {
      return;
    }

    const fieldMappings = [
      { targetAliases: ['goms'], responseAliases: ['goms'] },
      { targetAliases: ['quoteyear'], responseAliases: ['quoteyear'] },
      { targetAliases: ['quoteregion'], responseAliases: ['quoteregion'] },
      {
        targetAliases: ['priceadminowner', 'pricingadminowner'],
        responseAliases: ['priceadminowner', 'pricingadminowner'],
      },
    ];

    const dropdownSelections = fieldMappings.flatMap((mapping) => {
      const targetField = this.formFields.find((field) => {
        const keyToken = this.normalizeCompactToken(field?.key);
        const labelToken = this.normalizeCompactToken(field?.label);
        return mapping.targetAliases.includes(keyToken) || mapping.targetAliases.includes(labelToken);
      });
      const responseValue = this.getValueByNormalizedKey(responseRecord, mapping.responseAliases);

      return targetField && responseValue !== undefined
        ? [{ fieldKey: targetField.key, responseValue }]
        : [];
    });

    if (!dropdownSelections.length) {
      return;
    }

    const dropdownLoads = dropdownSelections.map((selection) =>
      this.fetchFieldDropdownOptions(selection.fieldKey).pipe(
        map((options) => ({ ...selection, options })),
      ),
    );

    this.quoteDetailDropdownSubscription?.unsubscribe();
    this.quoteDetailDropdownSubscription = forkJoin(dropdownLoads).subscribe((loadedDropdowns) => {
      for (const { fieldKey, responseValue, options } of loadedDropdowns) {
        const matchingOption = options.find((option) =>
          this.dropdownOptionMatchesReturnedValue(fieldKey, option, responseValue),
        );

        if (matchingOption) {
          this.editableRecord[fieldKey] = this.getFieldDropdownValue(matchingOption);
        }
      }

      this.customCdr.detectChanges();
    });
  }

  private dropdownOptionMatchesReturnedValue(fieldKey: string, option: any, responseValue: any): boolean {
    const optionValue = this.getFieldDropdownValue(option);
    if (this.compareFieldDropdownValues(optionValue, responseValue)) {
      return true;
    }

    const optionTokens = this.getDropdownMatchTokens([
      optionValue,
      option?.value,
      option?.data,
      option?.label,
      this.getFieldDropdownLabelForField(fieldKey, option),
    ]);
    const responseTokens = this.getDropdownMatchTokens([responseValue]);
    return Array.from(responseTokens).some((token) => optionTokens.has(token));
  }

  private getDropdownMatchTokens(values: any[]): Set<string> {
    const tokens = new Set<string>();
    const comparableObjectKeys = new Set([
      'id',
      'code',
      'value',
      'name',
      'label',
      'displayname',
      'goms',
      'quoteyear',
      'quoteregion',
      'priceadminowner',
      'pricingadminowner',
    ]);

    const collect = (value: any): void => {
      if (value === null || value === undefined || value === '') {
        return;
      }
      if (typeof value !== 'object') {
        tokens.add(String(value).trim().toLowerCase());
        return;
      }

      Object.entries(value).forEach(([key, nestedValue]) => {
        if (comparableObjectKeys.has(this.normalizeCompactToken(key))) {
          collect(nestedValue);
        }
      });
    };

    values.forEach(collect);
    return tokens;
  }

  private findQuoteDetailResponseRecord(response: any): Record<string, any> | null {
    const queue: any[] = [response];
    let bestMatch: Record<string, any> | null = null;
    let bestScore = 0;
    const responseFieldAliases = new Set([
      'goms',
      'quoteyear',
      'quoteregion',
      'priceadminowner',
      'pricingadminowner',
    ]);

    while (queue.length) {
      const candidate = queue.shift();
      if (!candidate || typeof candidate !== 'object') {
        continue;
      }

      if (Array.isArray(candidate)) {
        queue.push(...candidate);
        continue;
      }

      const score = Object.keys(candidate)
        .map((key) => this.normalizeCompactToken(key))
        .filter((key) => responseFieldAliases.has(key)).length;
      if (score > bestScore) {
        bestMatch = candidate;
        bestScore = score;
      }

      queue.push(...Object.values(candidate).filter((value) => value && typeof value === 'object'));
    }

    return bestMatch;
  }

  private getValueByNormalizedKey(record: any, aliases: string[]): any {
    if (!record || typeof record !== 'object') {
      return undefined;
    }

    const aliasSet = new Set(aliases);
    const matchingKey = Object.keys(record).find((key) => aliasSet.has(this.normalizeCompactToken(key)));
    return matchingKey === undefined ? undefined : record[matchingKey];
  }

  private normalizeCompactToken(value: any): string {
    return String(value ?? '').trim().toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  isFieldReadonly(field: any): boolean {
    if (this.isPaHistoryReadOnly()) {
      return true;
    }

    const fieldKey = typeof field === 'string' ? field : field?.key;
    const fieldLabel = typeof field === 'string' ? field : field?.label;
    if (!fieldKey && !fieldLabel) {
      return false;
    }

    if (field?.readonly === true || field?.readOnly === true || field?.disabled === true) {
      return true;
    }

    const configuredFields = [
      ...(Array.isArray(this.data?.readonlyFields) ? this.data.readonlyFields : []),
      ...(Array.isArray(this.data?.readOnlyFields) ? this.data.readOnlyFields : []),
      ...(Array.isArray(this.data?.disabledFields) ? this.data.disabledFields : []),
      ...this.resolveEntityReadonlyFields(),
    ];
    const configuredTokens = new Set(configuredFields.map((value) => this.normalizeHiddenFieldToken(value)));

    return [fieldKey, fieldLabel]
      .some((candidate) => configuredTokens.has(this.normalizeHiddenFieldToken(candidate)));
  }

  isPaHistoryReadOnly(): boolean {
    return RER_PA_HISTORY_ENTITIES.has(
      String(this.resolveSelectedEntityKeyForHeaderOrder() || '').trim().toLowerCase(),
    );
  }

  isFormActionDisabled(action: any): boolean {
    const actionId = String(action?.actionId || '').trim().toLowerCase();
    return this.isSaving ||
      (this.isPaHistoryReadOnly() && (actionId === 'save' || actionId === 'reset'));
  }

  private resolveEntityReadonlyFields(): any[] {
    const entityKey = this.resolveSelectedEntityKeyForHeaderOrder();
    const readonlyByEntity =
      this.data?.readonlyFieldsByEntity ??
      this.data?.readOnlyFieldsByEntity ??
      this.data?.disabledFieldsByEntity;

    if (!entityKey || !readonlyByEntity || typeof readonlyByEntity !== 'object' || Array.isArray(readonlyByEntity)) {
      return [];
    }

    const normalizedEntityKey = String(entityKey).trim().toLowerCase();
    const matchedFields = Object.entries(readonlyByEntity).find(
      ([configuredEntity]) => String(configuredEntity || '').trim().toLowerCase() === normalizedEntityKey,
    )?.[1];

    return Array.isArray(matchedFields) ? matchedFields : [];
  }

  private applySpecialFormFieldLabels(): void {
    if (!Array.isArray(this.formFields)) {
      return;
    }

    this.formFields = this.formFields.map((field) => {
      const token = this.normalizeQuoteFieldToken(field?.key).replace(/\s+/g, '');
      const isPaHistory = RER_PA_HISTORY_ENTITIES.has(
        String(this.resolveSelectedEntityKeyForHeaderOrder() || '').trim().toLowerCase(),
      );
      if (isPaHistory && token === 'currentprice') {
        return { ...field, label: 'New Price' };
      }
      if (isPaHistory && token === 'currenteffectivedate') {
        return { ...field, label: 'New Effective Date' };
      }
      if (token === 'shiptocodecustomername') {
        return { ...field, label: 'Ship To Code Customer Name' };
      }
      return field;
    });
  }

  private applyPreferredFieldOrder(): void {
    if (!Array.isArray(this.formFields) || this.formFields.length < 2) {
      return;
    }

    const orderIndex = new Map(
      SideEntityFormComponent.PREFERRED_FIELD_ORDER.map((field, index) => [field, index]),
    );
    this.formFields = this.formFields
      .map((field, originalIndex) => ({ field, originalIndex }))
      .sort((left, right) => {
        const leftOrder = this.resolvePreferredFieldOrderIndex(left.field, orderIndex);
        const rightOrder = this.resolvePreferredFieldOrderIndex(right.field, orderIndex);
        if (leftOrder !== undefined && rightOrder !== undefined) {
          return leftOrder - rightOrder;
        }
        if (leftOrder !== undefined) {
          return -1;
        }
        if (rightOrder !== undefined) {
          return 1;
        }
        return left.originalIndex - right.originalIndex;
      })
      .map(({ field }) => field);
  }

  private resolvePreferredFieldOrderIndex(field: any, orderIndex: Map<string, number>): number | undefined {
    for (const candidate of [field?.key, field?.label]) {
      let token = this.normalizeOrderToken(candidate).replace(/\s*\/\s*/g, ' ');
      if (token === 'approval status') {
        token = 'approvals status';
      }
      const index = orderIndex.get(token);
      if (index !== undefined) {
        return index;
      }
    }
    return undefined;
  }

  isQuoteYesNoField(fieldKey: string): boolean {
    if (!this.isQuoteForm() || this.isQuoteActionField(fieldKey)) {
      return false;
    }

    if (this.isConfiguredQuoteYesNoField(fieldKey)) {
      return true;
    }

    const component = this as any;
    const values = [
      component.editableRecord?.[fieldKey],
      component.originalRecord?.[fieldKey],
    ];

    return values.some((value) => {
      const normalized = String(value ?? '').trim().toLowerCase();
      return normalized === 'yes' || normalized === 'no';
    });
  }

  getQuoteYesNoOptions(fieldKey: string): Array<{ label: string; value: any }> {
    const configuredField = this.getConfiguredQuoteYesNoField(fieldKey);
    const configuredOptions = configuredField ?
      configuredField.options ||
        configuredField.dropdown?.options ||
        this.data?.quoteYesNoDropdown?.options ||
        this.data?.quoteYesNoOptions :
      null;

    if (Array.isArray(configuredOptions) && configuredOptions.length) {
      const optionsCacheKey = JSON.stringify(configuredOptions);
      if (!this.quoteYesNoOptionsCache.has(optionsCacheKey)) {
        this.quoteYesNoOptionsCache.set(optionsCacheKey, configuredOptions
          .map((option: any) => {
            if (option && typeof option === 'object') {
              const label = option.label ?? option.name ?? option.displayName ?? option.display_name ?? option.value;
              const value = option.value ?? option.id ?? option.code ?? label;
              return {
                label: String(label ?? ''),
                value,
              };
            }

            return {
              label: String(option ?? ''),
              value: option,
            };
          })
          .filter((option) => option.label));
      }

      return this.quoteYesNoOptionsCache.get(optionsCacheKey) || this.defaultQuoteYesNoOptions;
    }

    return this.defaultQuoteYesNoOptions;
  }

  getQuoteYesNoSelectedValue(fieldKey: string): any {
    const currentValue = (this as any).editableRecord?.[fieldKey];
    const options = this.getQuoteYesNoOptions(fieldKey);
    const matchedOption = options.find((option) =>
      this.normalizeQuoteFieldToken(option.value) === this.normalizeQuoteFieldToken(currentValue) ||
      this.normalizeQuoteFieldToken(option.label) === this.normalizeQuoteFieldToken(currentValue),
    );

    return matchedOption ? matchedOption.value : currentValue;
  }

  private isConfiguredQuoteYesNoField(fieldKey: string): boolean {
    return this.getConfiguredQuoteYesNoFieldTokens().has(this.normalizeQuoteFieldToken(fieldKey));
  }

  private getConfiguredQuoteYesNoField(fieldKey: string): any | null {
    const token = this.normalizeQuoteFieldToken(fieldKey);
    this.getConfiguredQuoteYesNoFieldTokens();
    return this.quoteYesNoFieldConfigCache?.get(token) || null;
  }

  private getConfiguredQuoteYesNoFieldTokens(): Set<string> {
    const selectedEntityKey = this.resolveSelectedEntityKeyForSave();
    const cacheKey = String(selectedEntityKey || '').trim().toLowerCase();
    if (this.quoteYesNoFieldTokenCache && this.quoteYesNoFieldTokenCacheKey === cacheKey) {
      return this.quoteYesNoFieldTokenCache;
    }

    this.quoteYesNoFieldTokenCacheKey = cacheKey;
    this.quoteYesNoFieldConfigCache = new Map<string, any>();
    const entity = this.normalizeQuoteFieldToken(selectedEntityKey);
    const configuredByEntity =
      this.data?.quoteYesNoFieldsByEntity ||
      this.data?.yesNoFieldsByEntity ||
      {};
    const configuredFields = [
      ...(Array.isArray(this.data?.quoteYesNoFields) ? this.data.quoteYesNoFields : []),
      ...(Array.isArray(this.data?.yesNoFields) ? this.data.yesNoFields : []),
      ...(Array.isArray(configuredByEntity?.[entity]) ? configuredByEntity[entity] : []),
      ...(selectedEntityKey && Array.isArray(configuredByEntity?.[selectedEntityKey]) ?
        configuredByEntity[selectedEntityKey] : []),
    ];
    this.quoteYesNoFieldTokenCache = new Set<string>();

    configuredFields.forEach((field: any) => {
      if (field && typeof field === 'object') {
        const fieldAliases = [
          field.key,
          field.label,
          field.name,
          ...(Array.isArray(field.keys) ? field.keys : []),
        ];
        fieldAliases.forEach((candidate) => {
          const token = this.normalizeQuoteFieldToken(candidate);
          if (token) {
            this.quoteYesNoFieldTokenCache?.add(token);
            this.quoteYesNoFieldConfigCache?.set(token, field);
          }
        });
        return;
      }

      const token = this.normalizeQuoteFieldToken(field);
      if (token) {
        this.quoteYesNoFieldTokenCache?.add(token);
        this.quoteYesNoFieldConfigCache?.set(token, { options: this.data?.quoteYesNoDropdown?.options || this.data?.quoteYesNoOptions });
      }
    });

    return this.quoteYesNoFieldTokenCache;
  }

  isQuoteActionField(fieldKey: string): boolean {
    return this.isQuoteForm() &&
      (this.isQuoteApprovalField(fieldKey) || this.isQuoteBillToShipToField(fieldKey));
  }

  isIconOnlyActionField(fieldKey: string): boolean {
    return this.isQuoteActionField(fieldKey) ||
      this.isQuoteSapConfigField(fieldKey) ||
      this.isBillToShipToDetailsField(fieldKey) ||
      this.isIntercompanyConfigurationsField(fieldKey);
  }

  getIconOnlyActionLabel(fieldKey: string): string {
    return this.getQuoteFieldPopupLabel(fieldKey);
  }

  getIconOnlyActionIcon(fieldKey: string): string {
    return this.getQuoteFieldPopupIcon(fieldKey);
  }

  openIconOnlyFieldAction(event: Event, fieldKey: string): void {
    this.openQuoteFieldPopup(event as MouseEvent, fieldKey);
  }

  isQuoteDetailPartNumberField(fieldKey: string): boolean {
    const entity = this.normalizeQuoteFieldToken(this.resolveSelectedEntityKeyForSave());
    const field = this.normalizeQuoteFieldToken(fieldKey);
    return (entity === 'quote details' || entity === 'cummins quote detail') && field === 'part number';
  }

  openPartNumberPopup(event: MouseEvent, fieldKey: string): void {
    event.preventDefault();
    event.stopPropagation();

    if (!this.partNumberDialog || this.isFieldReadonly(fieldKey)) {
      return;
    }

    this.activePartNumberFieldKey = fieldKey;
    this.partNumberCurrentPage = 1;
    this.pendingPartNumberOption = this.findPartNumberOptionForCurrentValue(fieldKey);
    this.setFieldDropdownSearchTerm(fieldKey, '');
    this.fetchFieldDropdownOptions(fieldKey, 1).subscribe();

    this.partNumberDialogRef = this.dialog.open(this.partNumberDialog, {
      width: 'min(920px, 92vw)',
      maxWidth: '92vw',
      maxHeight: '88vh',
      panelClass: 'part-number-dialog-panel',
      autoFocus: false,
      restoreFocus: true,
    });
    this.partNumberDialogRef.afterClosed().subscribe(() => {
      this.activePartNumberFieldKey = '';
      this.pendingPartNumberOption = null;
      this.partNumberDialogRef = undefined;
    });
  }

  selectPartNumberOption(option: any): void {
    this.pendingPartNumberOption = option;
  }

  get partNumberTotalRows(): number {
    return ((this as any).fieldDropdownOptions?.[this.activePartNumberFieldKey] || []).length;
  }

  get partNumberPageSize(): number {
    return this.resolvePartNumberLimit(
      this.appliedPartNumberFilterPayload?.requestData?.filters ||
      this.getPartNumberDefaultFilters(),
    );
  }

  get partNumberHasNextPage(): boolean {
    return this.partNumberTotalRows >= this.partNumberPageSize;
  }

  getPartNumberPageNumbers(): number[] {
    const windowSize = 5;
    const lastVisiblePage = this.partNumberHasNextPage
      ? this.partNumberCurrentPage + 1
      : this.partNumberCurrentPage;
    const startPage = Math.max(1, Math.min(this.partNumberCurrentPage - 2, lastVisiblePage - windowSize + 1));
    const endPage = Math.max(lastVisiblePage, Math.min(startPage + windowSize - 1, lastVisiblePage));

    return Array.from(
      { length: endPage - startPage + 1 },
      (_, index) => startPage + index,
    );
  }

  getPartNumberVisibleOptions(): any[] {
    if (!this.activePartNumberFieldKey || this.fieldDropdownLoading[this.activePartNumberFieldKey]) {
      return [];
    }

    return (this as any).fieldDropdownOptions?.[this.activePartNumberFieldKey] || [];
  }

  goToPartNumberPage(page: number): void {
    const nextPage = Math.max(1, page);
    if (nextPage === this.partNumberCurrentPage) {
      return;
    }

    document.querySelector<HTMLElement>('.part-number-table-wrap')?.scrollTo({ top: 0 });
    this.fetchFieldDropdownOptions(this.activePartNumberFieldKey, nextPage).subscribe();
  }

  readonly trackByPartNumberOption = (index: number, option: any): string => {
    return `${index}:${this.getPartNumberComparableCode(option)}`;
  };

  isPartNumberOptionSelected(option: any): boolean {
    if (!this.pendingPartNumberOption) {
      return false;
    }
    return this.getPartNumberComparableCode(option) ===
      this.getPartNumberComparableCode(this.pendingPartNumberOption);
  }

  submitPartNumberSelection(): void {
    if (!this.activePartNumberFieldKey || !this.pendingPartNumberOption) {
      return;
    }

    // Preserve the existing dropdown value shape so save/reset behaviour is unchanged.
    this.onFieldValueChange(
      this.activePartNumberFieldKey,
      this.getFieldDropdownValue(this.pendingPartNumberOption),
    );
    this.partNumberDialogRef?.close();
  }

  cancelPartNumberSelection(): void {
    this.partNumberDialogRef?.close();
  }

  hasPartNumberFilterApplied(): boolean {
    const filters = this.appliedPartNumberFilterPayload?.requestData?.filters;
    return Array.isArray(filters) && filters.length > 0;
  }

  openPartNumberFilterPopup(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    const fieldKey = this.activePartNumberFieldKey;
    if (!fieldKey) {
      return;
    }

    const component = this as any;
    const dropdownConfig = component.getFieldDropdownConfig(fieldKey);
    const lookupConfig = this.getPartNumberLookupConfig();
    const apiUrl = lookupConfig?.apiUrl || dropdownConfig?.apiUrl || this.data?.attributeValuesApiUrl || '/spriced/platform';
    const defaultApplyPayload = this.buildQuoteDetailPartNumberPayload(this.getPartNumberDefaultFilters(false));
    const dialogRef = this.dialog.open(SpricedFilterDialogComponent, {
      width: '60vw',
      maxWidth: '70vw',
      panelClass: 'spriced-filter-dialog-panel',
      data: {
        filterApiUrl: apiUrl,
        filterMethod: lookupConfig?.method || dropdownConfig?.method || 'post',
        filterHeaders: lookupConfig?.headers || dropdownConfig?.headers || this.data?.headers,
        domainApiUrl: apiUrl,
        domainPayloadTemplate: this.buildPartNumberFilterPayload('GenericAttributeValuesService'),
        defaultApplyPayload,
        submitFunctionName: 'GenericDynamicReadServices',
        domainFunctionName: 'GenericAttributeValuesService',
        selectedEntity: 'part',
        filterEntity: 'part',
        filterPayload: this.buildPartNumberFilterPayload('GenericEntityAttributesService'),
        appliedFilterPayload: this.appliedPartNumberFilterPayload,
        tableColumns: [
          { key: 'code', label: 'Code' },
          { key: 'name', label: 'Name' },
        ],
      },
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (!result) {
        return;
      }

      this.appliedPartNumberFilterPayload = {
        ...result,
        requestData: {
          ...(result.requestData || {}),
          entity: 'part',
          filters: this.normalizePartNumberAppliedFilters(result?.requestData?.filters),
        },
      };
      this.pendingPartNumberOption = null;
      this.partNumberCurrentPage = 1;
      component.fieldDropdownOptions[fieldKey] = [];
      this.fetchFieldDropdownOptions(fieldKey, 1).subscribe();
    });
  }

  clearPartNumberFilter(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    const fieldKey = this.activePartNumberFieldKey;
    if (!fieldKey) {
      return;
    }

    const component = this as any;
    this.appliedPartNumberFilterPayload = null;
    this.pendingPartNumberOption = this.findPartNumberOptionForCurrentValue(fieldKey);
    this.partNumberCurrentPage = 1;
    component.fieldDropdownOptions[fieldKey] = [];
    this.fetchFieldDropdownOptions(fieldKey, 1).subscribe();
  }

  getPartNumberOptionCode(option: any): string {
    const data = option?.data ?? option;
    return String(data?.code ?? data?.Code ?? option?.code ?? option?.Code ?? option?.value ?? '');
  }

  getPartNumberOptionName(option: any): string {
    const data = option?.data ?? option;
    return String(
      data?.name ?? data?.Name ?? data?.display_name ?? data?.displayName ??
      option?.name ?? option?.Name ?? option?.display_name ?? option?.displayName ?? '',
    );
  }

  private findPartNumberOptionForCurrentValue(fieldKey: string): any {
    const currentValue = (this as any).editableRecord?.[fieldKey];
    return ((this as any).fieldDropdownOptions?.[fieldKey] || []).find((option: any) =>
      this.getPartNumberComparableCode(option) === this.getPartNumberComparableCode(currentValue),
    ) ?? null;
  }

  private getPartNumberComparableCode(option: any): string {
    if (option === null || option === undefined) {
      return '';
    }
    if (typeof option !== 'object') {
      return String(option);
    }

    const data = option?.data ?? option;
    return String(
      data?.code ?? data?.Code ?? data?.id ?? data?.value ??
      option?.code ?? option?.Code ?? option?.id ?? option?.value ?? '',
    );
  }

  openQuoteFieldPopup(event: MouseEvent, fieldKey: string): void {
    event.preventDefault();
    event.stopPropagation();

    const tableWidgetId = (Array.isArray(this.data?.filterListner) ? this.data.filterListner[0] : null) ||
      'widget-entity-table';
    window.dispatchEvent(new CustomEvent(SideEntityFormComponent.QUOTE_FORM_POPUP_EVENT, {
      detail: {
        widgetId: tableWidgetId,
        type: this.getFormCellActionType(fieldKey),
        fieldKey,
        record: (this as any).editableRecord,
        saveLocally: this.isQuoteBillToShipToField(fieldKey),
      },
    }));
  }

  getQuoteFieldPopupLabel(fieldKey: string): string {
    if (this.isQuoteSapConfigField(fieldKey)) {
      return 'Add or edit Quote SAP configuration';
    }
    if (this.isBillToShipToDetailsField(fieldKey)) {
      return 'View Ship To code and customer details';
    }
    if (this.isIntercompanyConfigurationsField(fieldKey)) {
      return 'View configuration details';
    }
    return this.isQuoteApprovalField(fieldKey)
      ? 'View approval status'
      : 'View Bill To / Ship To details';
  }

  getQuoteFieldPopupIcon(fieldKey: string): string {
    if (this.isQuoteApprovalField(fieldKey)) {
      return 'task_alt';
    }
    return this.isQuoteSapConfigField(fieldKey) ? 'settings' : 'visibility';
  }

  private getFormCellActionType(fieldKey: string): string {
    if (this.isQuoteApprovalField(fieldKey)) {
      return 'approval-status';
    }
    if (this.isQuoteSapConfigField(fieldKey)) {
      return 'quote-sap-config';
    }
    if (this.isBillToShipToDetailsField(fieldKey)) {
      return 'ship-to-code-customer-name';
    }
    if (this.isIntercompanyConfigurationsField(fieldKey)) {
      return 'intercompany-configurations';
    }
    return 'bill-to-ship-to';
  }

  private ensureQuoteActionFields(): void {
    const component = this as any;
    if (!this.isQuoteForm() || !component.editableRecord || !Array.isArray(component.formFields)) {
      return;
    }

    const existingKeys = new Set(component.formFields.map((field: any) => field?.key));
    const approvalKey = Object.keys(component.editableRecord).find((key) => this.isQuoteApprovalField(key));
    if (approvalKey && !existingKeys.has(approvalKey)) {
      component.formFields.push({ key: approvalKey, label: 'Approval Status' });
    }
  }

  private isQuoteForm(): boolean {
    const entity = this.normalizeQuoteFieldToken(this.resolveSelectedEntityKeyForSave());
    return entity === 'quote' || entity === 'cummins quote' || entity === 'quote detail' || entity === 'cummins quote detail';
  }

  private isQuoteApprovalField(fieldKey: string): boolean {
    const token = this.normalizeQuoteFieldToken(fieldKey);
    return token === 'approval status' || token === 'approvals status';
  }

  private isQuoteBillToShipToField(fieldKey: string): boolean {
    const token = this.normalizeQuoteFieldToken(fieldKey);
    return token.includes('bill to') && token.includes('ship to');
  }

  private isQuoteSapConfigField(fieldKey: string): boolean {
    if (this.normalizeQuoteFieldToken(this.resolveSelectedEntityKeyForSave()) !==
      'cummins bill to ship to config') {
      return false;
    }

    return this.normalizeQuoteFieldToken(fieldKey).replace(/\s+/g, '') === 'quotesapconfig';
  }

  private isBillToShipToDetailsField(fieldKey: string): boolean {
    if (this.normalizeQuoteFieldToken(this.resolveSelectedEntityKeyForSave()) !==
      'cummins bill to ship to config') {
      return false;
    }

    const token = this.normalizeQuoteFieldToken(fieldKey).replace(/\s+/g, '');
    return token === 'shiptocodecustomername' ||
      token === 'shiptocode' ||
      token === 'customername';
  }

  private isIntercompanyConfigurationsField(fieldKey: string): boolean {
    if (this.normalizeQuoteFieldToken(this.resolveSelectedEntityKeyForSave()) !==
      'cummins intercompany configuration') {
      return false;
    }

    const token = this.normalizeQuoteFieldToken(fieldKey);
    return token === 'configuration' || token === 'configurations';
  }

  private normalizeQuoteFieldToken(value: any): string {
    return String(value ?? '')
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/[^a-zA-Z0-9]+/g, ' ')
      .trim()
      .toLowerCase();
  }

  private applyResponseFieldOrder(): void {
    if (!this.editableRecord || !Array.isArray(this.formFields) || this.formFields.length < 2) {
      return;
    }

    const responseOrder = new Map<string, number>();
    Object.keys(this.editableRecord).forEach((key, index) => {
      responseOrder.set(this.normalizeOrderToken(key), index);
    });

    this.formFields = this.formFields
      .map((field, originalIndex) => ({ field, originalIndex }))
      .sort((left, right) => {
        const leftIndex = responseOrder.get(this.normalizeOrderToken(left.field?.key));
        const rightIndex = responseOrder.get(this.normalizeOrderToken(right.field?.key));

        if (leftIndex !== undefined && rightIndex !== undefined) {
          return leftIndex - rightIndex;
        }
        if (leftIndex !== undefined) {
          return -1;
        }
        if (rightIndex !== undefined) {
          return 1;
        }
        return left.originalIndex - right.originalIndex;
      })
      .map(({ field }) => field);
  }

  override onFieldDropdownOpened(fieldKey: string, isOpen = true): void {
    if (!isOpen || !fieldKey) {
      return;
    }

    const component = this as any;
    const config = component.getFieldDropdownConfig(fieldKey);
    if (!config?.apiUrl) {
      return;
    }

    const isPaginatedPartNumber = this.isQuoteDetailPartNumberField(fieldKey);
    const requestKey = !isPaginatedPartNumber
      ? component.getFieldDropdownRequestKey(fieldKey)
      : null;
    if (
      component.fieldDropdownLoading[fieldKey] ||
      (requestKey && component.loadedFieldDropdownKeys?.has(requestKey))
    ) {
      return;
    }

    this.fetchFieldDropdownOptions(fieldKey).subscribe();
  }

  private fetchFieldDropdownOptions(fieldKey: string, partNumberPage = this.partNumberCurrentPage): Observable<any[]> {
    const component = this as any;
    const config = component.getFieldDropdownConfig(fieldKey);
    if (!config?.apiUrl) {
      return of(component.fieldDropdownOptions?.[fieldKey] || []);
    }

    const isPaginatedPartNumber = this.isQuoteDetailPartNumberField(fieldKey);
    component.fieldDropdownLoading[fieldKey] = true;
    const partNumberLookupConfig = isPaginatedPartNumber
      ? this.getPartNumberLookupConfig()
      : null;
    const payload = this.isQuoteDetailPartNumberField(fieldKey)
      ? this.buildQuoteDetailPartNumberPayload(undefined, partNumberPage)
      : component.buildFieldDropdownPayload(fieldKey, config);

    const loadOptions = (requestPayload: any): Observable<any[]> => component.configService.getDropdownOptions(
        partNumberLookupConfig?.apiUrl || config.apiUrl,
        requestPayload,
        partNumberLookupConfig?.method || config.method || 'post',
        partNumberLookupConfig?.headers || config.headers || this.data?.headers,
      ).pipe(
        map((response: any) => {
        // Part Number can contain hundreds of thousands of values. Keep the API rows
        // by reference and page them in the dialog instead of cloning every row into
        // dropdown wrappers and then rendering the entire collection.
          return isPaginatedPartNumber
            ? this.extractDropdownRowsFromResponse(response, fieldKey)
            : this.normalizeDropdownOptionsForField(response, fieldKey);
        }),
      );

    const loadParentFallback = (): Observable<any[]> => {
      if (isPaginatedPartNumber || !component.domainAttributeEntities?.has(fieldKey)) {
        return of([]);
      }

      const fallbackPayload = component.buildFieldDropdownPayload(fieldKey, config, true);
      return fallbackPayload?.requestData?.entity !== payload?.requestData?.entity
        ? loadOptions(fallbackPayload).pipe(catchError(() => of([])))
        : of([]);
    };

    return loadOptions(payload).pipe(
      catchError(() => loadParentFallback()),
      switchMap((options: any[]) => {
        if (options.length > 0 || isPaginatedPartNumber || !component.domainAttributeEntities?.has(fieldKey)) {
          return of(options);
        }

        return loadParentFallback();
      }),
      map((options: any[]) => {
        component.fieldDropdownOptions[fieldKey] = options;
        if (!this.isQuoteDetailPartNumberField(fieldKey)) {
          const requestKey = component.getFieldDropdownRequestKey(fieldKey);
          component.loadedFieldDropdownKeys?.add(requestKey);
        }
        component.fieldDropdownLoading[fieldKey] = false;
        if (this.isQuoteDetailPartNumberField(fieldKey)) {
          this.partNumberCurrentPage = partNumberPage;
        }
        if (fieldKey === this.activePartNumberFieldKey && !this.pendingPartNumberOption) {
          this.pendingPartNumberOption = this.findPartNumberOptionForCurrentValue(fieldKey);
        }
        this.publishFormSchemaToTable();
        return options;
      }),
      catchError(() => {
        component.fieldDropdownOptions[fieldKey] = [];
        component.fieldDropdownLoading[fieldKey] = false;
        return of([]);
      }),
    );
  }

  private buildQuoteDetailPartNumberPayload(filters?: any[], page = this.partNumberCurrentPage): any {
    const configuredPayload = this.getPartNumberLookupPayloadTemplate();
    const payload = configuredPayload || {
      requestData: {
        entity: 'part',
        filters: this.getPartNumberDefaultFilters(),
      },
      responseList: [],
      entity: 'product',
      workflowId: 'UI',
      endpoints: [
        {
          functionName: 'GenericDynamicReadServices',
          version: '1.0',
        },
      ],
      communicationType: 'rest',
      version: '1.0',
      functionName: 'CumminsQuoteDetailEnrichment',
    };

    const sourceFilters =
      filters ||
      this.appliedPartNumberFilterPayload?.requestData?.filters ||
      payload?.requestData?.filters ||
      this.getPartNumberDefaultFilters();

    payload.requestData = {
      ...(payload.requestData || {}),
      entity: 'part',
      filters: this.applyPartNumberPageToFilters(sourceFilters, page),
    };

    return payload;
  }

  private getPartNumberDefaultFilters(includeUuidFilter = true): any[] {
    const configuredFilters = this.getPartNumberLookupPayloadTemplate()?.requestData?.filters;
    const filters: any[] = Array.isArray(configuredFilters)
      ? JSON.parse(JSON.stringify(configuredFilters))
      : [
        {
          attributeName: 'limit',
          operator: 'is',
          logicalOperator: '',
          beginScope: '',
          endScope: '',
          attributeValue: 50,
        },
        {
          attributeName: 'offset',
          operator: 'is',
          logicalOperator: '',
          beginScope: '',
          endScope: '',
          attributeValue: 0,
        },
        {
          attributeName: 'order by',
          operator: 'is',
          logicalOperator: '',
          beginScope: '',
          endScope: '',
          attributeValue: 'uuid',
        },
      ];

    if (
      includeUuidFilter &&
      !filters.some((filter) => this.normalizeAttributeName(filter?.attributeName) === 'uuid')
    ) {
      filters.push({
        attributeName: 'uuid',
        operator: 'is_not_null',
        attributeValue: 'null',
        attributeType: '',
        logicalOperator: '',
        beginScope: '',
        endScope: '',
      });
    }

    return includeUuidFilter
      ? filters
      : filters.filter((filter) => this.normalizeAttributeName(filter?.attributeName) !== 'uuid');
  }

  private getPartNumberLookupPayloadTemplate(): any | null {
    const lookupConfig = this.getPartNumberLookupConfig();
    const payload =
      lookupConfig?.customPayload ||
      lookupConfig?.payload ||
      lookupConfig?.requestPayload ||
      null;

    return payload && typeof payload === 'object'
      ? JSON.parse(JSON.stringify(payload))
      : null;
  }

  private getPartNumberLookupConfig(): any {
    return this.data?.partNumberLookup || this.data?.partNumberLookupConfig || {};
  }

  private applyPartNumberPageToFilters(filters: any[], page: number): any[] {
    const nextFilters = JSON.parse(JSON.stringify(Array.isArray(filters) ? filters : []));
    const limit = this.resolvePartNumberLimit(nextFilters);
    const offset = (Math.max(1, page) - 1) * limit;

    this.upsertPartNumberPagingFilter(nextFilters, 'limit', limit);
    this.upsertPartNumberPagingFilter(nextFilters, 'offset', offset);

    return nextFilters;
  }

  private resolvePartNumberLimit(filters: any[]): number {
    const limitFilter = Array.isArray(filters)
      ? filters.find((filter) => this.normalizeAttributeName(filter?.attributeName) === 'limit')
      : null;
    const configuredLimit = Number(limitFilter?.attributeValue);

    return Number.isFinite(configuredLimit) && configuredLimit > 0 ? configuredLimit : 50;
  }

  private upsertPartNumberPagingFilter(filters: any[], attributeName: 'limit' | 'offset', attributeValue: number): void {
    const filter = filters.find((candidate) => this.normalizeAttributeName(candidate?.attributeName) === attributeName);
    if (filter) {
      filter.attributeValue = attributeValue;
      return;
    }

    filters.push({
      attributeName,
      operator: 'is',
      logicalOperator: '',
      beginScope: '',
      endScope: '',
      attributeValue,
    });
  }

  private normalizeAttributeName(attributeName: any): string {
    return String(attributeName || '').trim().toLowerCase();
  }

  private normalizePartNumberAppliedFilters(filters: any): any[] {
    if (!Array.isArray(filters)) {
      return this.getPartNumberDefaultFilters(false);
    }

    return filters.map((filter: any) => ({
      ...filter,
      operator: this.normalizePartNumberFilterOperator(filter?.operator),
    }));
  }

  private normalizePartNumberFilterOperator(operator: any): string {
    const normalizedOperator = String(operator || '').trim();
    const operatorMap: Record<string, string> = {
      is_equal_to: 'Is equal',
      is_not_equal_to: 'Is not equal',
    };

    return operatorMap[normalizedOperator] || normalizedOperator;
  }

  private buildPartNumberFilterPayload(functionName: string): any {
    return {
      requestData: {
        entity: 'part',
      },
      responseList: [],
      entity: 'product',
      workflowId: 'UI',
      endpoints: [
        {
          functionName,
          version: '1.0',
        },
      ],
      communicationType: 'rest',
      version: '1.0',
      functionName,
    };
  }

  override getFieldDisplayLabel(fieldKey: string): string {
    const value = (this as any).editableRecord?.[fieldKey];
    if (this.shouldHideRerEnginePricingEmptyFuelRating(fieldKey, value)) {
      return '';
    }
    const dropdown = (this as any).getFieldDropdownConfig(fieldKey);

    if (!dropdown) {
      return this.formatFieldValueForField(fieldKey, value);
    }

    const selectedValue = this.getSelectedDomainFieldValue(fieldKey);
    const selectedOption = ((this as any).fieldDropdownOptions?.[fieldKey] || []).find((option: any) =>
      this.compareFieldDropdownValues(this.getFieldDropdownValueForField(fieldKey, option), selectedValue),
    );

    if (selectedOption) {
      return this.getFieldDropdownLabelForField(fieldKey, selectedOption);
    }

    return this.formatFieldValueForField(fieldKey, value);
  }

  getFieldDisplayLabelForField(fieldKey: string): string {
    const value = (this as any).editableRecord?.[fieldKey];
    if (this.shouldHideRerEnginePricingEmptyFuelRating(fieldKey, value)) {
      return '';
    }
    const dropdown = (this as any).getFieldDropdownConfig(fieldKey);
    const selectedValue = this.getSelectedDomainFieldValue(fieldKey);
    const selectedOption = (this as any).fieldDropdownOptions?.[fieldKey]?.find((option: any) =>
      this.compareFieldDropdownValues(this.getFieldDropdownValueForField(fieldKey, option), selectedValue),
    );

    if (selectedOption) {
      return this.getFieldDropdownLabelForField(fieldKey, selectedOption);
    }

    return this.formatFieldValueForField(fieldKey, value);
  }

  override getFieldDropdownLabel(option: any): string {
    return this.formatAttributeLabel(option);
  }

  override getFieldDropdownValue(option: any): any {
    if (!option || typeof option !== 'object') {
      return option;
    }

    return option?.data ?? option;
  }

  getFieldDropdownLabelForField(_fieldKey: string, option: any): string {
    return this.getFieldDropdownLabel(option);
  }

  formatFieldValueForField(fieldKey: string, value: any): string {
    if (this.shouldHideRerEnginePricingEmptyFuelRating(fieldKey, value)) {
      return '';
    }
    return this.formatAttributeLabel(value);
  }

  private shouldHideRerEnginePricingEmptyFuelRating(fieldKey: string, value: any): boolean {
    const entity = String(this.resolveSelectedEntityKeyForSave() || '').trim().toLowerCase();
    return (
      ['custom_rer_engine_pricing', 'cu_rer_engine_pricing', 'rer engine pricing'].includes(entity) &&
      String(fieldKey || '').trim().toLowerCase() === 'fuel_rating_hp' &&
      String(value ?? '').trim().toUpperCase() === 'EMPTY'
    );
  }

  override saveForm(action: any): void {
    if (this.isPaHistoryReadOnly()) {
      return;
    }

    if (!action.apiUrl) {
      console.error('Save API URL is not defined.');
      return;
    }

    const component = this as any;
    this.applyNumericFormattingOnSave(component.editableRecord);
    component.isSaving = true;
    const method = action.method || 'POST';
    const selectedEntityKey = this.resolveSelectedEntityKeyForSave();
    const configuredSaveTemplate = this.resolveConfiguredSaveTemplate(action, selectedEntityKey);

    const isNewRecord =
      component.originalRecord === null ||
      component.originalRecord === undefined ||
      component.originalRecord.id === null;

    const primaryId =
      component.originalRecord?.Code ??
      component.originalRecord?.code ??
      component.editableRecord?.Code ??
      component.editableRecord?.code ??
      null;

    if (!configuredSaveTemplate && !isNewRecord && (primaryId === null || primaryId === undefined || primaryId === '')) {
      component.isSaving = false;
      component.snackBar.open(
        'Cannot save: primaryId (Code) is missing.',
        'Close',
        this.getBottomRightSnackBarConfig(),
      );
      return;
    }

    const buildChangedFields = (editable: any, original: any): Record<string, any> => {
      const changed: Record<string, any> = {};
      const editableKeys = Object.keys(editable || {});
      for (const key of editableKeys) {
        if (!component.valuesEqualForSave(editable?.[key], original?.[key])) {
          changed[key] = this.normalizeFieldValueForPayload(editable?.[key], key);
        }
      }
      return changed;
    };

    const changedFields = isNewRecord ? null : buildChangedFields(component.editableRecord, component.originalRecord);
    let hasChanges = false;

    if (!isNewRecord) {
      hasChanges = !!changedFields && Object.keys(changedFields).length > 0;
    } else {
      hasChanges = Object.keys(component.editableRecord || {}).length > 0;
    }

    if (!hasChanges && action?.allowUnchangedSave !== true) {
      component.isSaving = false;
      component.snackBar.open(
        'No changes to save.',
        'Close',
        this.getBottomRightSnackBarConfig(),
      );
      return;
    }

    const dataForCreate = this.normalizeRecordForSave(component.editableRecord);
    delete (dataForCreate as any).id;

    const configuredSavePayload = this.buildConfiguredSavePayloadWithDynamicEntity(
      action,
      isNewRecord,
      changedFields,
    );
    const payload = configuredSavePayload || (isNewRecord
      ? { data: dataForCreate }
      : { primaryId, data: this.normalizeRecordForSave(component.editableRecord) });

    const globalFilters = component.globalFilterService.getCurrentFilters();
    const queryParams = { ...(globalFilters?.queryParams || {}) };
    if (!configuredSavePayload && !queryParams['entity']) {
      const entityFromRequestParams = globalFilters?.requestParams?.['entity'];
      if (entityFromRequestParams) {
        queryParams['entity'] = entityFromRequestParams;
      }
    }

    const saveApiUrl = this.resolveSaveApiUrlForEntity(action, selectedEntityKey);
    const filtersToSend = configuredSavePayload ? {} : { queryParams, requestParams: payload };
    const requestConfig = configuredSavePayload
      ? {
        ...component.data,
        ...action,
        apiUrl: saveApiUrl,
        rawPayload: payload,
        skipDefaultPayload: true,
        embedQueryParams: false,
        embeddedParams: [],
        useQueryParam: false,
      }
      : { ...component.data, ...action, skipDefaultPayload: true };

    this.debugLog('save_payload_built', {
      selectedEntityKey,
      saveApiUrl,
      payloadRequestEntity: payload?.requestData?.entity,
      payload,
    });

    const followUpHandler = getEntityFollowUpSaveHandler(selectedEntityKey);
    if (followUpHandler?.skipGenericSave) {
      // For this entity, skip the generic GenericSaveService call and save
      // directly via the follow-up function using the current form data.
      const directFollowUpPayload = this.buildFollowUpSavePayload(
        null,
        configuredSaveTemplate,
        selectedEntityKey,
        action,
        payload?.requestData,
      );
      const directRequestConfig = {
        ...requestConfig,
        rawPayload: directFollowUpPayload,
      };

      this.debugLog('save_skip_generic_dispatch', {
        selectedEntityKey,
        directFollowUpPayload,
      });

      this.executeConfiguredSaveRequest(saveApiUrl, {}, directRequestConfig, method).subscribe({
        next: (response: any) => {
          this.debugLog('save_skip_generic_success', { selectedEntityKey, response });
          this.finishSuccessfulSave(action, selectedEntityKey, isNewRecord);
        },
        error: (error: any) => {
          component.isSaving = false;
          console.error('Save failed', error);
          this.dispatchRerEnginePricingRefreshAfterSaveAttempt(action, selectedEntityKey, isNewRecord);
          const errorMessage = this.resolveRerSaveErrorMessage(error, selectedEntityKey) || 'Error saving data.';
          component.snackBar.open(
            errorMessage,
            'Close',
            this.getBottomRightSnackBarConfig(
              errorMessage !== 'Error saving data.' ? 10000 : undefined,
              errorMessage !== 'Error saving data.' ? ['error-snackbar'] : undefined,
            ),
          );
        },
      });
      return;
    }

    this.executeConfiguredSaveRequest(saveApiUrl, filtersToSend, requestConfig, method).subscribe({
      next: (response: any) => {
        const responseErrorMessage = this.resolveRerSaveErrorMessage(response, selectedEntityKey, false);
        if (responseErrorMessage) {
          component.isSaving = false;
          this.debugLog('save_primary_business_error', {
            selectedEntityKey,
            response,
            errorMessage: responseErrorMessage,
          });
          this.dispatchRerEnginePricingRefreshAfterSaveAttempt(action, selectedEntityKey, isNewRecord);
          component.snackBar.open(
            responseErrorMessage,
            'Close',
            this.getBottomRightSnackBarConfig(10000, ['error-snackbar']),
          );
          return;
        }

        const sequentialRequired = this.requiresSequentialFollowUpSave(selectedEntityKey);
        this.debugLog('save_primary_success', {
          selectedEntityKey,
          sequentialRequired,
          response,
        });

        if (!sequentialRequired) {
          this.finishSuccessfulSave(action, selectedEntityKey, isNewRecord);
          return;
        }

        const followUpPayload = this.buildFollowUpSavePayload(
          response,
          configuredSaveTemplate,
          selectedEntityKey,
          action,
        );
        const followUpRequestConfig = {
          ...requestConfig,
          rawPayload: followUpPayload,
        };

        this.debugLog('save_follow_up_dispatch', {
          selectedEntityKey,
          followUpPayload,
        });

        this.executeConfiguredSaveRequest(saveApiUrl, {}, followUpRequestConfig, method).subscribe({
          next: (followUpResponse: any) => {
            this.debugLog('save_follow_up_success', {
              selectedEntityKey,
              response: followUpResponse,
            });
            this.finishSuccessfulSave(action, selectedEntityKey, isNewRecord);
          },
          error: (error: any) => {
            component.isSaving = false;
            console.error('Follow-up save failed', error);
            this.dispatchRerEnginePricingRefreshAfterSaveAttempt(action, selectedEntityKey, isNewRecord);
            component.snackBar.open(
              'Error saving data.',
              'Close',
              this.getBottomRightSnackBarConfig(),
            );
          },
        });
      },
      error: (error: any) => {
        component.isSaving = false;
        console.error('Save failed', error);
        this.dispatchRerEnginePricingRefreshAfterSaveAttempt(action, selectedEntityKey, isNewRecord);
        const errorMessage = this.resolveRerSaveErrorMessage(error, selectedEntityKey) || 'Error saving data.';
        if (errorMessage !== 'Error saving data.') {
          component.snackBar.open(
            errorMessage,
            'Close',
            this.getBottomRightSnackBarConfig(10000, ['error-snackbar']),
          );
          return;
        }
        component.snackBar.open(
          'Error saving data.',
          'Close',
          this.getBottomRightSnackBarConfig(),
        );
      },
    });
  }

  private buildConfiguredSavePayloadWithDynamicEntity(
    action: any,
    isNewRecord: boolean,
    changedFields: Record<string, any> | null,
  ): any | null {
    const component = this as any;
    const selectedEntityKey = this.resolveSelectedEntityKeyForSave();
    const template = this.resolveConfiguredSaveTemplate(action, selectedEntityKey);
    if (!template) {
      return null;
    }

    const payload = JSON.parse(JSON.stringify(template));
    const fullRowSaveValue = this.buildFullRowSaveValue(isNewRecord, changedFields);
    const requestDataValue = action?.saveVisibleFieldsOnly === true
      ? this.pickVisibleFormFieldsForSave(fullRowSaveValue, action)
      : fullRowSaveValue;
    payload.requestData = {
      ...(payload.requestData || {}),
      ...(requestDataValue && typeof requestDataValue === 'object' ? requestDataValue : {}),
    };
    if (payload?.requestData && Object.prototype.hasOwnProperty.call(payload.requestData, 'value')) {
      delete payload.requestData.value;
    }
    payload.requestData = this.normalizeUuidFieldsForPayload(payload.requestData);

    if (this.isGomsCustomerSavePayload(payload)) {
      payload.requestData = this.normalizeGomsCustomerSaveRequestData(
        payload.requestData,
        component.editableRecord,
      );
    }

    if (selectedEntityKey && action?.includeEntityInRequestData !== false) {
      payload.requestData.entity = selectedEntityKey;
    }

    payload.functionName = payload.functionName || action?.functionName || component.data?.functionName || 'GenericSaveService';
    payload.entity = payload.entity || component.data?.entity || 'product';
    payload.workflowId = payload.workflowId || component.data?.workflowId || 'UI';
    payload.communicationType = payload.communicationType || 'rest';
    payload.version = payload.version || action?.version || component.data?.version || '1.0';

    if (payload.functionName === 'GenericSaveService' || this.isGomsCustomerSavePayload(payload)) {
      const userEmail = this.getLoggedInUserEmailForOverride();
      this.debugLog('save_user_email_resolved', {
        userEmail,
        requestDataBeforeAssign: payload.requestData,
      });

      if (userEmail) {
        payload.requestData = {
          ...(payload.requestData || {}),
          user_id: userEmail,
        };
        this.debugLog('save_user_id_assigned', {
          requestDataAfterAssign: payload.requestData,
        });
      } else {
        this.debugLog('save_user_email_missing', {
          requestDataUnchanged: payload.requestData,
        });
      }
    }

    if (!Array.isArray(payload.endpoints) || payload.endpoints.length === 0) {
      payload.endpoints = [
        {
          functionName: payload.functionName,
          version: payload.version,
        },
      ];
    } else {
      payload.endpoints = payload.endpoints.map((endpoint: any, index: number) => {
        if (index !== 0 || !endpoint || typeof endpoint !== 'object') {
          return endpoint;
        }

        return {
          ...endpoint,
          functionName: endpoint.functionName || payload.functionName,
          version: endpoint.version || payload.version,
        };
      });
    }

    if (action?.omitTopLevelFunctionName === true) {
      delete payload.functionName;
    }

    this.debugLog('save_payload_entity_applied', {
      selectedEntityKey,
      payloadRequestEntity: payload.requestData.entity,
      payloadRequestData: payload.requestData,
    });

    return payload;
  }

  private isGomsCustomerSavePayload(payload: any): boolean {
    return Array.isArray(payload?.endpoints) && payload.endpoints.some((endpoint: any) =>
      String(endpoint?.functionName || '').trim().toLowerCase() === 'gomscustomerservice'
    );
  }

  private normalizeGomsCustomerSaveRequestData(
    requestData: Record<string, any>,
    editableRecord: Record<string, any> | null | undefined,
  ): Record<string, any> {
    const normalizedRequestData = { ...(requestData || {}) };
    Object.keys(normalizedRequestData).forEach((fieldKey) => {
      if (fieldKey === 'goms' || fieldKey === 'user_id') {
        return;
      }

      const sourceValue = editableRecord?.[fieldKey] ?? normalizedRequestData[fieldKey];
      normalizedRequestData[fieldKey] = fieldKey === 'oem_flag'
        ? this.resolveGomsCustomerOemId(sourceValue)
        : this.coerceGomsCustomerStringValue(sourceValue);
    });

    const rawGomsValue = editableRecord?.['goms'] ?? normalizedRequestData['goms'];
    normalizedRequestData['goms'] = this.coerceGomsCustomerNumberValue(rawGomsValue);

    return normalizedRequestData;
  }

  private coerceGomsCustomerStringValue(value: any): string | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const data = value && typeof value === 'object' ? value?.data ?? value : null;
    const scalarValue = data
      ? data?.name ??
      data?.label ??
      data?.display_name ??
      data?.displayName ??
      data?.code ??
      data?.value
      : value;

    if (scalarValue === null || scalarValue === undefined || scalarValue === '') {
      return null;
    }

    return String(scalarValue);
  }

  private resolveGomsCustomerOemId(value: any): any {
    return this.getGomsCustomerOemCode(value);
  }

  private coerceGomsCustomerNumberValue(value: any): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const data = value && typeof value === 'object' ? value?.data ?? value : null;
    const scalarValue = data
      ? data?.value ?? data?.goms ?? data?.id ?? data?.code ?? data?.name
      : value;
    const numericValue = Number(scalarValue);

    return Number.isFinite(numericValue) ? numericValue : null;
  }

  private resolveConfiguredSaveTemplate(action: any, selectedEntityKey: string | null): any | null {
    const normalizedEntityKey = String(selectedEntityKey || '').trim().toLowerCase();
    const savePayloadByEntity = action?.savePayloadByEntity;

    if (normalizedEntityKey && savePayloadByEntity && typeof savePayloadByEntity === 'object' && !Array.isArray(savePayloadByEntity)) {
      const matchedTemplate = Object.entries(savePayloadByEntity).find(
        ([entityKey]) => String(entityKey || '').trim().toLowerCase() === normalizedEntityKey,
      )?.[1];

      if (matchedTemplate && typeof matchedTemplate === 'object') {
        return matchedTemplate;
      }
    }

    return action?.savePayload || action?.customPayload || action?.rawPayload || null;
  }

  private pickVisibleFormFieldsForSave(row: Record<string, any>, action?: any): Record<string, any> {
    const component = this as any;
    const configuredSaveFieldKeys = Array.isArray(action?.saveFieldKeys)
      ? action.saveFieldKeys.map((key: any) => String(key || '').trim()).filter(Boolean)
      : [];
    const visibleFieldKeys = new Set<string>(
      (configuredSaveFieldKeys.length > 0
        ? configuredSaveFieldKeys
        : (Array.isArray(component.formFields) ? component.formFields : [])
          .map((field: any) => String(field?.key || '').trim())
          .filter(Boolean)),
    );

    return Object.entries(row || {}).reduce((visibleRow: Record<string, any>, [key, value]) => {
      if (visibleFieldKeys.has(key) && !this.isQuoteApprovalField(key)) {
        visibleRow[key] = value;
      }
      return visibleRow;
    }, {});
  }

  private getLoggedInUserEmailForOverride(): string | null {
    const keycloak = this.keycloakServiceOverride.getKeycloakInstance();
    const tokenParsed: any = keycloak?.tokenParsed ?? {};
    const preferredUsername =
      typeof tokenParsed?.preferred_username === 'string'
        ? tokenParsed.preferred_username.trim()
        : '';

    const emailCandidates = [
      tokenParsed?.email,
      preferredUsername.includes('@') ? preferredUsername : null,
    ];

    for (const candidate of emailCandidates) {
      if (typeof candidate === 'string' && candidate.trim()) {
        this.debugLog('save_keycloak_email_candidate_selected', {
          email: candidate.trim(),
          tokenParsed,
        });
        return candidate.trim();
      }
    }

    this.debugLog('save_keycloak_email_missing', {
      tokenParsed,
      subject: keycloak?.subject,
    });
    return null;
  }

  private applyConfiguredFieldOrder(): void {
    const entityKey = this.resolveSelectedEntityKeyForHeaderOrder();
    if (!entityKey || !Array.isArray(this.formFields) || !this.formFields.length) {
      return;
    }

    this.loadHeaderOrder(entityKey).subscribe((configuredOrder: string[] | null) => {
      if (!configuredOrder?.length || !Array.isArray(this.formFields) || !this.formFields.length) {
        return;
      }

      const orderIndex = new Map<string, number>();
      configuredOrder.forEach((fieldKey, index) => {
        orderIndex.set(this.normalizeOrderToken(fieldKey), index);
      });

      this.formFields = [...this.formFields].sort((left, right) => {
        const leftIndex = this.resolveConfiguredOrderIndex(left, orderIndex);
        const rightIndex = this.resolveConfiguredOrderIndex(right, orderIndex);

        if (leftIndex !== undefined && rightIndex !== undefined) {
          return leftIndex - rightIndex;
        }

        if (leftIndex !== undefined) {
          return -1;
        }

        if (rightIndex !== undefined) {
          return 1;
        }

        return 0;
      });

      this.customCdr.detectChanges();
    });
  }

  private resolveConfiguredOrderIndex(field: any, orderIndex: Map<string, number>): number | undefined {
    const candidates = [
      field?.key,
      field?.label,
    ];

    for (const candidate of candidates) {
      const normalizedCandidate = this.normalizeOrderToken(candidate);
      if (!normalizedCandidate) {
        continue;
      }

      const matchedIndex = orderIndex.get(normalizedCandidate);
      if (matchedIndex !== undefined) {
        return matchedIndex;
      }
    }

    return undefined;
  }

  private normalizeOrderToken(value: any): string {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ');
  }

  private resolveSelectedEntityKeyForHeaderOrder(): string | null {
    const selectedEntityKey = this.resolveSelectedEntityKeyForSave();
    return selectedEntityKey ? String(selectedEntityKey).trim() : null;
  }

  private loadHeaderOrder(entityKey: string): Observable<string[] | null> {
    const normalizedEntityKey = String(entityKey || '').trim();
    const inlineOrder = this.resolveInlineHeaderOrder(normalizedEntityKey);
    if (inlineOrder?.length) {
      return of(inlineOrder);
    }

    if (this.data?.headerOrderLookup === false ||
      ['cu_supp_s_goms_customer', 's_goms_customer', 's_goms customer'].includes(normalizedEntityKey.toLowerCase())) {
      return of(null);
    }

    const assetEntityKey = this.resolveHeaderOrderAssetEntityKey(normalizedEntityKey);
    if (!assetEntityKey) {
      return of(null);
    }

    const cachedOrder = this.headerOrderCache.get(assetEntityKey);
    if (cachedOrder) {
      return cachedOrder;
    }

    const orderRequest = this.customHttp
      .get<any>(`assets/header-attribute/${assetEntityKey}.json`)
      .pipe(
        map((config) => this.parseHeaderOrder(config)),
        catchError(() => of(null)),
        shareReplay(1),
      );
    this.headerOrderCache.set(assetEntityKey, orderRequest);
    return orderRequest;
  }

  private resolveHeaderOrderAssetEntityKey(entityKey: string): string {
    const normalizedEntityKey = String(entityKey || '').trim().toLowerCase();
    const aliases: Record<string, string> = {
      custom_rer_engine_pricing: 'cu_rer_engine_pricing',
      custom_rer_suggested: 'cu_suggested_prices_rer_engine',
    };
    return aliases[normalizedEntityKey] || normalizedEntityKey;
  }

  private resolveInlineHeaderOrder(entityKey: string): string[] | null {
    const normalizedEntityKey = String(entityKey || '').trim().toLowerCase();
    const orderByEntity =
      this.data?.fieldOrderByEntity ??
      this.data?.columnOrderByEntity ??
      this.data?.orderByEntity;

    if (normalizedEntityKey && orderByEntity && typeof orderByEntity === 'object' && !Array.isArray(orderByEntity)) {
      const matchedOrder = Object.entries(orderByEntity).find(
        ([configuredEntity]) => String(configuredEntity || '').trim().toLowerCase() === normalizedEntityKey,
      )?.[1];
      const entityOrder = this.parseHeaderOrder(matchedOrder);
      if (entityOrder?.length) {
        return entityOrder;
      }
    }

    return this.parseHeaderOrder(this.data);
  }

  private parseHeaderOrder(config: any): string[] | null {
    const configuredOrder = Array.isArray(config)
      ? config
      : config?.columnOrder ?? config?.fieldOrder ?? config?.order;

    if (!Array.isArray(configuredOrder)) {
      return null;
    }

    const normalizedOrder = configuredOrder
      .map((value: any) => String(value || '').trim())
      .filter(Boolean);

    return normalizedOrder.length ? normalizedOrder : null;
  }

  private buildFullRowSaveValue(
    isNewRecord: boolean,
    changedFields: Record<string, any> | null,
  ): Record<string, any> {
    const component = this as any;
    const baseRow = this.normalizeRecordForSave(component.originalRecord);
    const editedRow = this.normalizeRecordForSave(component.editableRecord);
    const normalizedChangedFields =
      changedFields && typeof changedFields === 'object'
        ? this.normalizeRecordForSave(changedFields)
        : {};
    const fullRow = isNewRecord
      ? editedRow
      : {
        ...baseRow,
        ...editedRow,
        ...(normalizedChangedFields && typeof normalizedChangedFields === 'object' ? normalizedChangedFields : {}),
      };

    delete (fullRow as any).id;
    delete (fullRow as any).__isDraftNewRecord;

    const identifier =
      component.originalRecord?.uuid ??
      component.editableRecord?.uuid ??
      component.originalRecord?.id ??
      component.editableRecord?.id ??
      null;

    if (fullRow['uuid'] === undefined) {
      fullRow['uuid'] = identifier ?? null;
    }
    this.normalizeUuidFieldOnRecord(fullRow);

    this.debugLog('save_full_row_value_built', {
      isNewRecord,
      changedFieldKeys: Object.keys(changedFields || {}),
      originalRow: baseRow,
      editedRow,
      payloadValue: fullRow,
    });

    return fullRow;
  }

  private normalizeRecordForSave(record: any): Record<string, any> {
    if (!record || typeof record !== 'object' || Array.isArray(record)) {
      return {};
    }

    const normalized: Record<string, any> = {};
    Object.keys(record).forEach((key) => {
      normalized[key] = this.normalizeFieldValueForPayload(record[key], key);
    });
    delete normalized['__isDraftNewRecord'];
    this.normalizeUuidFieldOnRecord(normalized);

    this.debugLog('record_normalized_for_save', {
      inputKeys: Object.keys(record),
      blankStringKeysConvertedToNull: Object.keys(normalized).filter((key) => normalized[key] === null && this.isBlankString(record[key])),
      normalized,
    });

    return normalized;
  }

  private normalizeFieldValueForPayload(value: any, fieldKey?: string): any {
    const component = this as any;
    const normalizedValue = component.normalizeValueForSave(value);
    const roundedValue = this.shouldPreserveStringFieldInForm(fieldKey)
      ? normalizedValue
      : this.roundNumericValueForSave(normalizedValue);
    const sanitizedValue = this.sanitizeEmptyValues(roundedValue);

    if (this.containsBlankString(normalizedValue)) {
      this.debugLog('blank_value_sanitized', {
        originalValue: value,
        normalizedValue,
        sanitizedValue,
      });
    }

    return sanitizedValue;
  }

  private normalizeUuidFieldsForPayload(payload: any): any {
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
      return payload;
    }

    const normalizedPayload = { ...payload };
    this.normalizeUuidFieldOnRecord(normalizedPayload);
    return normalizedPayload;
  }

  private normalizeUuidFieldOnRecord(record: Record<string, any>): void {
    if (!record || typeof record !== 'object' || Array.isArray(record)) {
      return;
    }

    if (!Object.prototype.hasOwnProperty.call(record, 'uuid')) {
      return;
    }

    record['uuid'] = this.coerceUuidToString(record['uuid']);
  }

  private coerceUuidToString(value: any): any {
    if (value === null || value === undefined || typeof value === 'string') {
      return value;
    }

    if (typeof value === 'number' || typeof value === 'bigint' || typeof value === 'boolean') {
      return String(value);
    }

    if (typeof value === 'object') {
      const candidateValues = [
        value?.value,
        value?.uuid,
        value?.id,
      ];

      for (const candidate of candidateValues) {
        if (
          candidate !== null &&
          candidate !== undefined &&
          (typeof candidate === 'string' || typeof candidate === 'number' || typeof candidate === 'bigint' || typeof candidate === 'boolean')
        ) {
          return String(candidate);
        }
      }
    }

    return String(value);
  }

  private applyNumericFormattingOnSave(record: any): void {
    if (!record || typeof record !== 'object' || Array.isArray(record)) {
      return;
    }

    Object.keys(record).forEach((key) => {
      if (!this.shouldPreserveStringFieldInForm(key)) {
        record[key] = this.roundNumericValueForSave(record[key]);
      }
    });
  }

  private roundNumericValueForSave(value: any): any {
    if (value === null || value === undefined) {
      return value;
    }

    if (Array.isArray(value)) {
      return value.map((item) => this.roundNumericValueForSave(item));
    }

    if (typeof value === 'number') {
      return this.roundNumberToTwoDecimals(value);
    }

    if (typeof value === 'string') {
      const trimmedValue = value.trim();
      if (!trimmedValue) {
        return value;
      }

      if (!this.isPlainNumericString(trimmedValue)) {
        return value;
      }

      const parsedValue = Number(trimmedValue);
      return Number.isFinite(parsedValue)
        ? this.roundNumberToTwoDecimals(parsedValue)
        : value;
    }

    if (typeof value !== 'object') {
      return value;
    }

    const roundedObject: Record<string, any> = {};
    Object.keys(value).forEach((key) => {
      roundedObject[key] = this.roundNumericValueForSave(value[key]);
    });

    return roundedObject;
  }

  private roundNumberToTwoDecimals(value: number): number {
    if (!Number.isFinite(value)) {
      return value;
    }

    if (Number.isInteger(value)) {
      return value;
    }

    return Number(value.toFixed(2));
  }

  private isPlainNumericString(value: string): boolean {
    return /^-?\d+(?:\.\d+)?$/.test(value);
  }

  private shouldPreserveStringFieldInForm(fieldKey?: string): boolean {
    const token = this.normalizeQuoteFieldToken(fieldKey);
    return !!token && this.getFormStringFieldTokens().has(token);
  }

  private getFormStringFieldTokens(): Set<string> {
    const selectedEntityKey = this.resolveSelectedEntityKeyForSave();
    const cacheKey = String(selectedEntityKey || '').trim().toLowerCase();
    if (this.formStringFieldTokenCache && this.formStringFieldTokenCacheKey === cacheKey) {
      return this.formStringFieldTokenCache;
    }

    this.formStringFieldTokenCacheKey = cacheKey;
    const entity = this.normalizeQuoteFieldToken(selectedEntityKey);
    const configuredByEntity =
      this.data?.payloadStringFieldsByEntity ||
      this.data?.preserveFormStringFieldsByEntity ||
      this.data?.formStringFieldsByEntity ||
      this.data?.preserveStringFieldsByEntity ||
      this.data?.stringFieldsByEntity ||
      {};
    const configuredFields = [
      ...(Array.isArray(this.data?.payloadStringFields) ? this.data.payloadStringFields : []),
      ...(Array.isArray(this.data?.preserveFormStringFields) ? this.data.preserveFormStringFields : []),
      ...(Array.isArray(this.data?.formStringFields) ? this.data.formStringFields : []),
      ...(Array.isArray(this.data?.preserveStringFields) ? this.data.preserveStringFields : []),
      ...(Array.isArray(this.data?.stringFields) ? this.data.stringFields : []),
      ...(Array.isArray(configuredByEntity?.[entity]) ? configuredByEntity[entity] : []),
      ...(selectedEntityKey && Array.isArray(configuredByEntity?.[selectedEntityKey]) ?
        configuredByEntity[selectedEntityKey] : []),
    ];

    this.formStringFieldTokenCache = new Set<string>();
    configuredFields.forEach((field: any) => {
      const candidates = field && typeof field === 'object'
        ? [
          field.key,
          field.label,
          field.name,
          ...(Array.isArray(field.keys) ? field.keys : []),
        ]
        : [field];

      candidates.forEach((candidate) => {
        const token = this.normalizeQuoteFieldToken(candidate);
        if (token) {
          this.formStringFieldTokenCache?.add(token);
        }
      });
    });

    return this.formStringFieldTokenCache;
  }

  private sanitizeEmptyValues(value: any): any {
    if (this.isBlankString(value)) {
      return null;
    }

    if (Array.isArray(value)) {
      return value.map((item) => this.sanitizeEmptyValues(item));
    }

    if (!value || typeof value !== 'object') {
      return value;
    }

    const sanitized: Record<string, any> = {};
    Object.keys(value).forEach((key) => {
      sanitized[key] = this.sanitizeEmptyValues(value[key]);
    });

    return sanitized;
  }

  private isBlankString(value: any): value is string {
    return typeof value === 'string' && value.trim() === '';
  }

  private containsBlankString(value: any): boolean {
    if (this.isBlankString(value)) {
      return true;
    }

    if (Array.isArray(value)) {
      return value.some((item) => this.containsBlankString(item));
    }

    if (!value || typeof value !== 'object') {
      return false;
    }

    return Object.keys(value).some((key) => this.containsBlankString(value[key]));
  }

  private resolveSelectedEntityKeyForSave(): string | null {
    const component = this as any;
    const globalFilters = component.globalFilterService.getCurrentFilters?.();
    const candidates = [
      this.resolveEntityKeyAlias(component.sectionTitle),
      component.selectedEntityValue,
      this.normalizeFilterCandidate(globalFilters?.requestParams?.entity),
      this.normalizeFilterCandidate(globalFilters?.queryParams?.entity),
      component.editableRecord?.entity,
      component.originalRecord?.entity,
      component.data?.requestData?.entity,
      component.data?.entityName,
      component.data?.entity,
    ];

    for (const candidate of candidates) {
      if (candidate !== null && candidate !== undefined) {
        const normalized = String(candidate).trim();
        if (normalized) {
          return normalized;
        }
      }
    }

    return null;
  }

  private resolveEntityKeyAlias(value: any): string | null {
    const normalizedValue = String(value || '').trim().toLowerCase();
    return SideEntityFormComponent.ENTITY_KEY_BY_DISPLAY_LABEL[normalizedValue] || null;
  }

  private resolveSaveApiUrlForEntity(action: any, selectedEntityKey: string | null): string {
    const normalizedEntityKey = String(selectedEntityKey || '').trim().toLowerCase();
    const apiUrlByEntity = action?.saveApiUrlByEntity ?? action?.apiUrlByEntity;
    if (apiUrlByEntity && typeof apiUrlByEntity === 'object' && !Array.isArray(apiUrlByEntity)) {
      const matchedApiUrl = Object.entries(apiUrlByEntity).find(
        ([entityKey]) => String(entityKey || '').trim().toLowerCase() === normalizedEntityKey,
      )?.[1];

      if (typeof matchedApiUrl === 'string' && matchedApiUrl.trim()) {
        return matchedApiUrl.trim();
      }
    }

    if (SideEntityFormComponent.GENERIC_SAVE_API_ENTITIES.has(normalizedEntityKey)) {
      return SideEntityFormComponent.GENERIC_SAVE_API_URL;
    }

    return action?.apiUrl;
  }

  private normalizeFilterCandidate(value: any): any {
    if (Array.isArray(value)) {
      return value.length > 0 ? value[0] : null;
    }
    return value;
  }

  private executeConfiguredSaveRequest(
    apiUrl: string,
    filtersToSend: any,
    requestConfig: any,
    method: string,
  ): Observable<any> {
    const component = this as any;
    return new Observable((observer) => {
      component.configService.getApi(apiUrl, filtersToSend, requestConfig, method).subscribe({
        next: (response: any) => {
          if (this.isExplicitSaveFailureResponse(response)) {
            const message =
              response?.ui_response ||
              response?.error ||
              response?.detail ||
              'Save failed.';
            observer.error({
              type: 'business',
              response,
              message: String(message),
            });
            return;
          }

          observer.next(response);
          observer.complete();
        },
        error: (error: any) => observer.error(error),
      });
    });
  }

  /**
   * Successful API responses can be unwrapped to the saved entity, whose
   * `status` property is business data (for example, "Active" or "Completed").
   * Only explicit failure values should turn a 2xx save response into an error.
   */
  private isExplicitSaveFailureResponse(response: any): boolean {
    if (response?.success === false || response?.ok === false) {
      return true;
    }

    if (typeof response?.status !== 'string') {
      return false;
    }

    const failureStatusPattern = /(?:^|[\s_-])(error|fail|failed|failure|unsuccessful)(?:$|[\s_.:-])/i;
    return failureStatusPattern.test(response.status.trim());
  }

  private requiresSequentialFollowUpSave(selectedEntityKey: string | null): boolean {
    return !!getEntityFollowUpSaveHandler(selectedEntityKey);
  }

  private buildFollowUpSavePayload(
    saveResponse: any,
    saveTemplate: any,
    selectedEntityKey: string | null,
    action: any,
    precomputedResponseValue?: any,
  ): any {
    const component = this as any;
    const responseValue = precomputedResponseValue !== undefined
      ? precomputedResponseValue
      : this.extractFollowUpRequestValue(saveResponse);
    const followUpHandler = getEntityFollowUpSaveHandler(selectedEntityKey);
    const followUpPayload = saveTemplate
      ? JSON.parse(JSON.stringify(saveTemplate))
      : {
        requestData: {},
        responseList: [],
        entity: component.data?.entity || 'product',
        workflowId: component.data?.workflowId || 'UI',
        endpoints: [],
        communicationType: component.data?.communicationType || 'rest',
        version: component.data?.version || action?.version || '1.0',
      };

    if (followUpHandler?.buildPayload) {
      return this.normalizeUuidFieldsForPayload(followUpHandler.buildPayload({
        action,
        componentData: component.data,
        responseValue,
        saveResponse,
        saveTemplate,
        selectedEntityKey,
      }));
    }

    followUpPayload.requestData = {
      ...(followUpPayload.requestData || {}),
      entity: selectedEntityKey || followUpPayload.requestData?.entity || '',
      ...(responseValue && typeof responseValue === 'object' ? responseValue : {}),
    };
    if (this.isRerEnginePricingSaveEntity(selectedEntityKey)) {
      followUpPayload.requestData.is_deleted = false;
    }
    if (followUpPayload?.requestData && Object.prototype.hasOwnProperty.call(followUpPayload.requestData, 'value')) {
      delete followUpPayload.requestData.value;
    }
    followUpPayload.requestData = this.normalizeUuidFieldsForPayload(followUpPayload.requestData);
    followUpPayload.responseList = [];
    followUpPayload.operation = null;
    followUpPayload.functionName = followUpHandler?.followUpFunctionName || followUpPayload.functionName;
    followUpPayload.entity = followUpPayload.entity || component.data?.entity || 'product';
    followUpPayload.workflowId = followUpPayload.workflowId || component.data?.workflowId || 'UI';
    followUpPayload.communicationType = followUpPayload.communicationType || 'rest';
    followUpPayload.version = followUpPayload.version || action?.version || component.data?.version || '1.0';

    if (Array.isArray(followUpPayload.endpoints) && followUpPayload.endpoints.length > 0) {
      followUpPayload.endpoints = followUpPayload.endpoints.map((endpoint: any, index: number) => {
        if (index !== 0 || !endpoint || typeof endpoint !== 'object') {
          return endpoint;
        }

        return {
          ...endpoint,
          functionName: followUpHandler?.followUpFunctionName || followUpPayload.functionName,
        };
      });
    } else {
      followUpPayload.endpoints = [
        {
          functionName: followUpHandler?.followUpFunctionName || followUpPayload.functionName,
          version: followUpPayload.version || '1.0',
        },
      ];
    }

    return followUpPayload;
  }

  private isRerEnginePricingSaveEntity(selectedEntityKey: string | null): boolean {
    const normalizedEntityKey = String(selectedEntityKey || '').trim().toLowerCase();
    return normalizedEntityKey === 'custom_rer_engine_pricing' ||
      normalizedEntityKey === 'cu_rer_engine_pricing';
  }

  private isRerSuggestedPriceSaveEntity(selectedEntityKey: string | null): boolean {
    const normalizedEntityKey = String(selectedEntityKey || '').trim().toLowerCase();
    return normalizedEntityKey === 'custom_rer_suggested' ||
      normalizedEntityKey === 'cu_suggested_prices_rer_engine';
  }

  private resolveRerSaveErrorMessage(
    error: any,
    selectedEntityKey: string | null,
    includeFallback = true,
  ): string | null {
    const response = error?.response ??
      (error?.error && typeof error.error === 'object' ? error.error : error);

    if (this.isRerSuggestedPriceSaveEntity(selectedEntityKey)) {
      const responseError = response?.error;
      if (typeof responseError === 'string' && responseError.trim()) {
        return responseError.trim();
      }
    }

    if (this.isRerEnginePricingSaveEntity(selectedEntityKey)) {
      const errorReason = Array.isArray(response?.errors)
        ? response.errors.find((item: any) =>
          typeof item?._error_reason === 'string' && item._error_reason.trim(),
        )?._error_reason
        : null;

      if (errorReason) {
        return errorReason.trim();
      }
    }

    if (!includeFallback) {
      return null;
    }

    if (typeof error?.error === 'string' && error.error.trim()) {
      return error.error.trim();
    }

    return error?.message ? String(error.message) : 'Error saving data.';
  }

  private extractFollowUpRequestValue(saveResponse: any): any {
    const responseEntries = Array.isArray(saveResponse?.responseList) ? saveResponse.responseList : [];

    for (const responseEntry of responseEntries) {
      if (!responseEntry || typeof responseEntry !== 'object') {
        continue;
      }

      for (const value of Object.values(responseEntry)) {
        if (!Array.isArray(value) || value.length === 0) {
          continue;
        }

        const extractedItems = value
          .map((item: any) => item?.data)
          .filter((item: any) => item !== null && item !== undefined);

        if (!extractedItems.length) {
          continue;
        }

        return extractedItems.length === 1 ? extractedItems[0] : extractedItems;
      }
    }

    return {};
  }

  private finishSuccessfulSave(action: any, selectedEntityKey: string | null, isNewRecord: boolean): void {
    const component = this as any;
    component.isSaving = false;
    component.snackBar.open(
      'Saved successfully!',
      'Close',
      this.getBottomRightSnackBarConfig(10000, ['success-snackbar']),
    );
    component.originalRecord = component.cloneRecord(component.editableRecord);
    this.dispatchPostSaveRefreshRequests(action, selectedEntityKey, {
      isNewRecord,
      recordSnapshot: component.cloneRecord(component.editableRecord),
    });
    if (action.refreshWidgets && Array.isArray(action.refreshWidgets)) {
      action.refreshWidgets.forEach((widgetId: string) => {
        component.globalFilterService.requestWidgetRefresh(widgetId);
      });
    }
  }

  private dispatchRerEnginePricingRefreshAfterSaveAttempt(
    action: any,
    selectedEntityKey: string | null,
    isNewRecord: boolean,
  ): void {
    const component = this as any;
    const refreshEntityKey = this.normalizeEntityKeyForService(
      selectedEntityKey || this.resolveSelectedEntityKeyForSave(),
    );
    if (refreshEntityKey !== 'custom_rer_engine_pricing' && refreshEntityKey !== 'cu_rer_engine_pricing') {
      return;
    }

    this.dispatchPostSaveRefreshRequests(action, refreshEntityKey, {
      isNewRecord,
      recordSnapshot: component.cloneRecord(component.editableRecord),
    });
  }

  private debugLog(event: string, details: any): void {
    console.log(`[${SideEntityFormComponent.DEBUG_LOG_KEY}] ${event}`, details);
  }

  private dispatchPostSaveRefreshRequests(
    action: any,
    selectedEntityKey: string | null,
    saveContext: { isNewRecord: boolean; recordSnapshot: any },
  ): void {
    type PostSaveRefreshRequest = {
      widgetId: string;
      requestData: any;
      apiUrl: string;
      method: string;
      saveContext?: {
        isNewRecord: boolean;
        recordSnapshot: any;
      };
    };

    const refreshRequests = Array.isArray(action?.refreshRequests) ? action.refreshRequests : [];
    refreshRequests
      .map((request: any) => this.normalizePostSaveRefreshRequest(request, selectedEntityKey, saveContext))
      .filter((request: PostSaveRefreshRequest | null): request is PostSaveRefreshRequest =>
        !!request?.widgetId && !!request?.requestData && !!request?.apiUrl)
      .forEach((request: PostSaveRefreshRequest) => {
        this.debugLog('post_save_refresh_dispatch', {
          selectedEntityKey,
          requestData: request.requestData,
        });
        this.executePostSaveRefreshRequest(request);
      });
  }

  private normalizePostSaveRefreshRequest(
    request: any,
    selectedEntityKey: string | null,
    saveContext?: { isNewRecord: boolean; recordSnapshot: any },
  ): {
    widgetId: string;
    requestData: any;
    apiUrl: string;
    method: string;
    saveContext?: { isNewRecord: boolean; recordSnapshot: any };
  } | null {
    if (!request || typeof request !== 'object') {
      return null;
    }

    const widgetId = String(request?.widgetId || request?.id || '').trim();
    const apiUrl = String(request?.apiUrl || request?.url || this.data?.apiUrl || '/spriced/platform').trim();
    const method = String(request?.method || 'POST').trim() || 'POST';
    const requestDataTemplate =
      request?.requestData ??
      request?.payload ??
      request?.rawPayload ??
      request?.customPayload;

    if (!widgetId || !apiUrl || !requestDataTemplate || typeof requestDataTemplate !== 'object') {
      return null;
    }

    const requestData = JSON.parse(JSON.stringify(requestDataTemplate));
    if (requestData?.requestData && selectedEntityKey) {
      requestData.requestData.entity = selectedEntityKey;
    }
    this.applyPostSaveRefreshFunctionName(requestData, selectedEntityKey);
    this.applyPostSaveRefreshOrderByOverride(requestData);

    return {
      widgetId,
      requestData,
      apiUrl,
      method,
      saveContext,
    };
  }

  private executePostSaveRefreshRequest(
    request: {
      widgetId: string;
      requestData: any;
      apiUrl: string;
      method: string;
      saveContext?: { isNewRecord: boolean; recordSnapshot: any };
    },
  ): void {
    const component = this as any;
    // Give the target table a chance to replace the configured/default refresh
    // payload with its currently applied filter payload. CustomEvent dispatch is
    // synchronous, so the updated request data is available before the API call.
    const preparationDetail = {
      phase: 'prepare',
      widgetId: request.widgetId,
      requestData: request.requestData,
    };
    window.dispatchEvent(new CustomEvent(SideEntityFormComponent.TABLE_REFRESH_EVENT, {
      detail: preparationDetail,
    }));
    request.requestData = preparationDetail.requestData;

    const requestConfig = {
      ...component.data,
      apiUrl: request.apiUrl,
      method: request.method,
      rawPayload: request.requestData,
      customPayload: request.requestData,
      skipDefaultPayload: true,
      embedQueryParams: false,
      embeddedParams: [],
      useQueryParam: false,
    };

    component.configService.getApi(
      request.apiUrl,
      {},
      requestConfig,
      request.method,
    ).subscribe({
      next: (response: any) => {
        this.debugLog('post_save_refresh_success', {
          widgetId: request.widgetId,
          requestData: request.requestData,
          response,
        });
        window.dispatchEvent(new CustomEvent(SideEntityFormComponent.TABLE_REFRESH_EVENT, {
          detail: {
            widgetId: request.widgetId,
            response,
            requestData: request.requestData,
            saveContext: request.saveContext,
          },
        }));
      },
      error: (error: any) => {
        console.error('Post-save refresh failed', error);
        this.debugLog('post_save_refresh_error', {
          widgetId: request.widgetId,
          requestData: request.requestData,
          error,
        });
      },
    });
  }

  private applyPostSaveRefreshOrderByOverride(requestData: any): void {
    const filters = requestData?.requestData?.filters;
    if (!Array.isArray(filters)) {
      return;
    }

    filters.forEach((filter: any) => {
      const attributeName = String(filter?.attributeName || '')
        .trim()
        .toLowerCase();
      if (attributeName === 'order by') {
        filter.attributeValue = 'created_date desc';
      }
    });
  }

  private applyPostSaveRefreshFunctionName(requestData: any, selectedEntityKey: string | null): void {
    const normalizedEntityKey = this.normalizeEntityKeyForService(
      selectedEntityKey || requestData?.requestData?.entity,
    );
    const functionName = SideEntityFormComponent.POST_SAVE_READ_FUNCTION_BY_ENTITY[normalizedEntityKey];
    if (!functionName || !Array.isArray(requestData?.endpoints)) {
      return;
    }

    requestData.endpoints = requestData.endpoints.map((endpoint: any, index: number) => {
      if (index !== 0 || !endpoint || typeof endpoint !== 'object') {
        return endpoint;
      }

      return {
        ...endpoint,
        functionName,
      };
    });
  }

  private normalizeEntityKeyForService(value: any): string {
    const normalizedValue = String(value || '').trim().toLowerCase();
    return SideEntityFormComponent.ENTITY_KEY_BY_DISPLAY_LABEL[normalizedValue] || normalizedValue;
  }

  private filterHiddenFormFields(): void {
    if (!Array.isArray((this as any).formFields)) {
      return;
    }

    const entityHiddenTokens = this.resolveEntityHiddenFieldTokens();
    const restrictToPaHistoryFields = RER_PA_HISTORY_ENTITIES.has(
      String(this.resolveSelectedEntityKeyForHeaderOrder() || '').trim().toLowerCase(),
    );

    (this as any).formFields = (this as any).formFields.filter((field: any) => {
      const candidates = [
        field?.key,
        field?.label,
      ];

      if (restrictToPaHistoryFields) {
        return candidates.some((candidate) =>
          SideEntityFormComponent.RER_PA_HISTORY_FIELD_TOKENS.has(this.normalizeHiddenFieldToken(candidate)),
        );
      }

      if (candidates.some((candidate) => this.isHiddenFieldToken(candidate))) {
        return false;
      }

      if (
        entityHiddenTokens &&
        candidates.some((candidate) => entityHiddenTokens.has(this.normalizeHiddenFieldToken(candidate)))
      ) {
        return false;
      }

      return true;
    });
  }

  private resolveEntityHiddenFieldTokens(): Set<string> | null {
    const entityKey = this.resolveSelectedEntityKeyForHeaderOrder();
    if (!entityKey) {
      return null;
    }

    return SideEntityFormComponent.ENTITY_HIDDEN_FIELD_TOKENS.get(entityKey.toLowerCase()) ?? null;
  }

  private applyKnownFieldOrderFallback(): void {
    if (!Array.isArray((this as any).formFields) || !(this as any).formFields.length) {
      return;
    }

    const normalizedLabels = new Set<string>(
      (this as any).formFields.map((field: any) => this.normalizeHiddenFieldToken(field?.label || field?.key)),
    );
    const fieldOrderMap = this.resolveKnownFieldOrderMap(normalizedLabels);
    if (!fieldOrderMap) {
      return;
    }

    (this as any).formFields = [...(this as any).formFields].sort((left: any, right: any) => {
      const leftOrder = this.resolveKnownFieldOrderIndex(left, fieldOrderMap);
      const rightOrder = this.resolveKnownFieldOrderIndex(right, fieldOrderMap);

      if (leftOrder !== undefined && rightOrder !== undefined) {
        return leftOrder - rightOrder;
      }

      if (leftOrder !== undefined) {
        return -1;
      }

      if (rightOrder !== undefined) {
        return 1;
      }

      return 0;
    });
  }

  private isHiddenFieldToken(value: any): boolean {
    const normalizedValue = this.normalizeHiddenFieldToken(value);
    return SideEntityFormComponent.ALWAYS_HIDDEN_FIELD_TOKENS.has(normalizedValue) ||
      SideEntityFormComponent.HIDDEN_FIELD_TOKENS.has(normalizedValue);
  }

  private resolveKnownFieldOrderMap(normalizedLabels: Set<string>): Map<string, number> | null {
    const knownFieldOrderMaps = [
      SideEntityFormComponent.FUEL_RATING_DEFINITION_FIELD_ORDER,
      SideEntityFormComponent.RER_PA_HISTORY_FIELD_ORDER,
    ];

    for (const fieldOrderMap of knownFieldOrderMaps) {
      const expectedLabels = Array.from(fieldOrderMap.keys());
      const hasAllExpectedFields = expectedLabels.every((label) => normalizedLabels.has(label));
      if (hasAllExpectedFields) {
        return fieldOrderMap;
      }
    }

    return null;
  }

  private resolveKnownFieldOrderIndex(field: any, fieldOrderMap: Map<string, number>): number | undefined {
    const candidates = [
      field?.label,
      field?.key,
    ];

    for (const candidate of candidates) {
      const normalized = this.normalizeHiddenFieldToken(candidate);
      const matchedOrder = fieldOrderMap.get(normalized);
      if (matchedOrder !== undefined) {
        return matchedOrder;
      }
    }

    return undefined;
  }

  private normalizeHiddenFieldToken(value: any): string {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ');
  }

  private formatAttributeLabel(option: any): string {
    if (option === null || option === undefined) {
      return '';
    }

    if (typeof option !== 'object') {
      return this.rewriteAttributeLabelString(String(option));
    }

    const data = option?.data ?? option;
    const code = data?.code ?? data?.Code ?? option?.code ?? option?.Code;
    const name =
      data?.name ??
      data?.Name ??
      data?.display_name ??
      data?.displayName ??
      option?.name ??
      option?.Name ??
      option?.display_name ??
      option?.displayName;

    if (code !== undefined && code !== null && name !== undefined && name !== null) {
      return `${code} {${name}}`;
    }

    const rawLabel =
      option?.label ??
      data?.label ??
      data?.name ??
      data?.display_name ??
      data?.displayName ??
      '';

    return this.rewriteAttributeLabelString(String(rawLabel || ''));
  }

  private rewriteDropdownOptionsForField(fieldKey: string, options: any[]): any[] {
    if (!Array.isArray(options)) {
      return options;
    }

    return options.map((option: any) => {
      const formattedLabel = this.formatAttributeLabel(option);
      if (!formattedLabel) {
        return option;
      }

      return {
        ...option,
        data: option?.data ?? option?.source ?? option,
        label: formattedLabel,
      };
    });
  }

  private normalizeDropdownOptionsForField(response: any, fieldKey: string): any[] {
    const rows = this.extractDropdownRowsFromResponse(response, fieldKey);
    const seen = new Set<string>();
    const options: any[] = [];

    rows.forEach((row: any) => {
      const normalizedOption = this.normalizeDropdownOptionForField(row, fieldKey);
      if (!normalizedOption) {
        return;
      }

      const dedupeKey = this.getComparableDropdownValue(normalizedOption.value);
      if (seen.has(dedupeKey)) {
        return;
      }

      seen.add(dedupeKey);
      options.push(normalizedOption);
    });

    return this.rewriteDropdownOptionsForField(fieldKey, options);
  }

  private extractDropdownRowsFromResponse(response: any, fieldKey: string): any[] {
    const rawRows =
      response?.responseList?.[0]?.[fieldKey] ||
      response?.responseList?.[0]?.data?.[0]?.[fieldKey] ||
      response?.responseList?.[0]?.data?.[fieldKey] ||
      response?.responseList?.[0]?.data?.[0]?.data ||
      response?.responseList?.[0]?.data ||
      response?.data?.[0]?.data ||
      response?.data ||
      response?.options ||
      [];

    return Array.isArray(rawRows) ? rawRows : [];
  }

  private normalizeDropdownOptionForField(option: any, fieldKey: string): { label: string; value: any; data: any } | null {
    if (option === null || option === undefined) {
      return null;
    }

    if (typeof option !== 'object') {
      const text = this.rewriteAttributeLabelString(String(option));
      return {
        label: text,
        value: option,
        data: option,
      };
    }

    const dropdownConfig = (this as any).getFieldDropdownConfig(fieldKey);
    const value = this.resolveDropdownOptionValue(option, dropdownConfig);
    const formattedLabel = this.formatAttributeLabel(option);
    const fallbackLabel = this.resolveDropdownOptionLabel(option, dropdownConfig);
    const label = formattedLabel || fallbackLabel;

    if (value === undefined || label === undefined || label === null || label === '') {
      return null;
    }

    return {
      label: String(label),
      value,
      data: option?.data ?? option,
    };
  }

  private resolveDropdownOptionValue(option: any, dropdownConfig?: any): any {
    const data = option?.data ?? option;
    const explicitValueKeys = [
      dropdownConfig?.dropdownValueKey,
      dropdownConfig?.valueKey,
      dropdownConfig?.idKey,
    ]
      .map((key: any) => String(key || '').trim())
      .filter(Boolean);

    for (const key of explicitValueKeys) {
      const resolved = data?.[key] ?? option?.[key];
      if (resolved !== undefined && resolved !== null && resolved !== '') {
        return resolved;
      }
    }

    return (
      data?.id ??
      option?.id ??
      data?.code ??
      option?.code ??
      data?.value ??
      option?.value ??
      data?.name ??
      option?.name ??
      option
    );
  }

  private resolveDropdownOptionLabel(option: any, dropdownConfig?: any): string {
    const data = option?.data ?? option;
    const explicitLabelKeys = [
      dropdownConfig?.dropdownLabelKey,
      dropdownConfig?.labelKey,
      dropdownConfig?.displayKey,
      dropdownConfig?.textKey,
    ]
      .map((key: any) => String(key || '').trim())
      .filter(Boolean);

    for (const key of explicitLabelKeys) {
      const resolved = data?.[key] ?? option?.[key];
      if (resolved !== undefined && resolved !== null && resolved !== '') {
        return this.rewriteAttributeLabelString(String(resolved));
      }
    }

    const fallback =
      data?.label ??
      option?.label ??
      data?.display_name ??
      option?.display_name ??
      data?.displayName ??
      option?.displayName ??
      data?.name ??
      option?.name ??
      data?.code ??
      option?.code ??
      data?.id ??
      option?.id ??
      data?.value ??
      option?.value ??
      '';

    return this.rewriteAttributeLabelString(String(fallback || ''));
  }

  private getComparableDropdownValue(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }

    if (typeof value !== 'object') {
      return String(value);
    }

    const data = value?.data ?? value;
    return String(
      data?.id ??
      value?.id ??
      data?.code ??
      value?.code ??
      data?.value ??
      value?.value ??
      data?.name ??
      value?.name ??
      JSON.stringify(value),
    );
  }

  private rewriteAttributeLabelString(value: string): string {
    const normalizedValue = String(value || '').trim();
    if (!normalizedValue) {
      return '';
    }

    const hyphenMatch = normalizedValue.match(/^([^{}]+?)\s+-\s+([^{}]+)$/);
    if (hyphenMatch) {
      const left = hyphenMatch[1]?.trim();
      const right = hyphenMatch[2]?.trim();
      if (left && right) {
        return `${left} {${right}}`;
      }
    }

    return normalizedValue.replace(/^([^{}]+?)\s*\{(.+)\}$/, '$1 {$2}');
  }

  override ngOnDestroy(): void {
    this.quoteDetailFetchSubscription?.unsubscribe();
    this.quoteDetailDropdownSubscription?.unsubscribe();
    super.ngOnDestroy();
  }

}
